import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  LayoutDashboard, User, FileText, Inbox, Send, LogOut,
  MapPin, Calendar, Users, DollarSign, ChevronRight, Clock,
  Plus, Check, X, Pencil, Camera, Trash2, Building2,
  Globe, Phone, Mail, Award, Languages, TrendingUp,
  ChevronLeft, Image, UserPlus, Briefcase, Star, Menu,
  ArrowUpRight, BarChart3, Eye, Wallet, Lock, Unlock,
  Zap, Loader2, CreditCard, ArrowDown, ArrowUp, Sparkles,
  EyeOff, AlertCircle, MessageCircle, Search, BookOpen, Receipt, ExternalLink, Printer
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SERVICES_OPTIONS = [
  'Safari', 'Mountain Climbing', 'Beach Holidays', 'Cultural Tours',
  'Wildlife Photography', 'Bird Watching', 'Diving & Snorkeling',
  'Honeymoon Packages', 'Family Tours', 'Budget Travel',
  'Luxury Tours', 'Walking Safaris', 'Balloon Safaris',
  'Historical Tours', 'Adventure Sports'
];

const LOCATIONS_OPTIONS = [
  'Serengeti', 'Ngorongoro', 'Kilimanjaro', 'Zanzibar',
  'Tarangire', 'Lake Manyara', 'Selous', 'Ruaha',
  'Mikumi', 'Arusha', 'Dar es Salaam', 'Pemba Island',
  'Mafia Island', 'Mahale', 'Katavi'
];

const statusColors = {
  pending: 'bg-amber-50 text-amber-700 border border-amber-200',
  broadcasted: 'bg-blue-50 text-blue-700 border border-blue-200',
  responded: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
  booked: 'bg-neutral-900 text-white',
  submitted: 'bg-blue-50 text-blue-700 border border-blue-200',
  selected: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
  rejected: 'bg-neutral-100 text-neutral-500',
  viewed: 'bg-purple-50 text-purple-700 border border-purple-200'
};

export default function OperatorDashboard() {
  const [activeView, setActiveView] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [trips, setTrips] = useState([]);
  const [myItineraries, setMyItineraries] = useState([]);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [editingProfile, setEditingProfile] = useState(false);
  const [showTeamDialog, setShowTeamDialog] = useState(false);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [wallet, setWallet] = useState(null);
  const [unlockedLeads, setUnlockedLeads] = useState([]);
  const [showTopupDialog, setShowTopupDialog] = useState(false);
  const [topupAmount, setTopupAmount] = useState('50');
  const [showPromoteDialog, setShowPromoteDialog] = useState(false);
  const [promoteItinerary, setPromoteItinerary] = useState(null);
  const [promoDuration, setPromoDuration] = useState(7);
  const [unlocking, setUnlocking] = useState(null);
  const [leadsPage, setLeadsPage] = useState(1);
  const [leadsPerPage, setLeadsPerPage] = useState(9);
  const [conversations, setConversations] = useState([]);
  const [opUnreadCount, setOpUnreadCount] = useState(0);
  const [activeConversation, setActiveConversation] = useState(null);
  const [opMessages, setOpMessages] = useState([]);
  const [opNewMessage, setOpNewMessage] = useState('');
  const [sendingMsg, setSendingMsg] = useState(false);
  const [tripFilter, setTripFilter] = useState('all');
  const [tripSort, setTripSort] = useState('newest');
  const [leadSearch, setLeadSearch] = useState('');
  const [rejectedLeads, setRejectedLeads] = useState([]);
  const [previewTrip, setPreviewTrip] = useState(null);
  const [previewTab, setPreviewTab] = useState('details'); // 'details' | 'proposal'
  const [previewProposal, setPreviewProposal] = useState(null);
  const opWsRef = useRef(null);
  const opMsgEndRef = useRef(null);
  const [showActivityCosts, setShowActivityCosts] = useState(false);
  const [txPage, setTxPage] = useState(1);
  const [txPerPage, setTxPerPage] = useState(10);
  const [isTyping, setIsTyping] = useState(false);
  const typingTimeoutRef = useRef(null);
  const [teamAccounts, setTeamAccounts] = useState([]);
  const [showTeamAccountDialog, setShowTeamAccountDialog] = useState(false);
  const [teamAccountForm, setTeamAccountForm] = useState({ name: '', phone: '', pin: '1234', team_roles: ['leads_agent'] });
  const [creatingTeamAccount, setCreatingTeamAccount] = useState(false);
  const [topupRequests, setTopupRequests] = useState([]);
  const [topupCredits, setTopupCredits] = useState('');
  const [generatedInvoice, setGeneratedInvoice] = useState(null);
  const [confirmedBookings, setConfirmedBookings] = useState([]);
  const [invoiceDialog, setInvoiceDialog] = useState(null);
  const [invoiceForm, setInvoiceForm] = useState({ cost_per_person_per_day: '', number_of_people: '', number_of_days: '', notes: '' });
  const [profilePreview, setProfilePreview] = useState(null);
  const [invoicePreview, setInvoicePreview] = useState(null);
  const [creditInvoicePreview, setCreditInvoicePreview] = useState(null);
  const [reviewDialog, setReviewDialog] = useState(null);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState('');
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const { user, logout, api } = useAuth();
  const navigate = useNavigate();

  const [profileForm, setProfileForm] = useState({});
  const [teamForm, setTeamForm] = useState({ name: '', role: '', bio: '' });
  const [itineraryForm, setItineraryForm] = useState({});

  const isApproved = profile?.is_approved || user?.is_verified || false;

  const fetchAll = useCallback(async () => {
    try {
      const [statsRes, tripsRes, itinRes, profileRes, walletRes, leadsRes, convRes, unreadRes, rejRes] = await Promise.allSettled([
        api.get('/operator/stats'),
        api.get('/trips'),
        api.get('/itineraries/my'),
        api.get('/operator/profile'),
        api.get('/operator/wallet'),
        api.get('/operator/leads/unlocked'),
        api.get('/conversations'),
        api.get('/conversations/unread-count'),
        api.get('/operator/leads/rejected')
      ]);
      if (statsRes.status === 'fulfilled') setStats(statsRes.value.data);
      if (tripsRes.status === 'fulfilled') setTrips(tripsRes.value.data);
      if (itinRes.status === 'fulfilled') setMyItineraries(itinRes.value.data);
      if (profileRes.status === 'fulfilled') setProfile(profileRes.value.data);
      if (walletRes.status === 'fulfilled') setWallet(walletRes.value.data);
      if (leadsRes.status === 'fulfilled') setUnlockedLeads(leadsRes.value.data);
      if (convRes.status === 'fulfilled') setConversations(convRes.value.data);
      if (unreadRes.status === 'fulfilled') setOpUnreadCount(unreadRes.value.data.total_unread || 0);
      if (rejRes.status === 'fulfilled') setRejectedLeads(rejRes.value.data);
      // Fetch team accounts and topup history
      try { const taRes = await api.get('/operator/team/accounts'); setTeamAccounts(taRes.data); } catch {}
      try { const tuRes = await api.get('/operator/wallet/topup-history'); setTopupRequests(tuRes.data); } catch {}
      try { const bkRes = await api.get('/bookings'); setConfirmedBookings(bkRes.data.filter(b => b.status === 'confirmed')); } catch {}
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => {
    const interval = setInterval(async () => {
      try { const r = await api.get('/conversations/unread-count'); setOpUnreadCount(r.data.total_unread || 0); } catch {}
    }, 15000);
    return () => clearInterval(interval);
  }, [api]);


  const previewTraveler = async (travelerId) => {
    try {
      const res = await api.get(`/profile/traveler/${travelerId}`);
      setProfilePreview({ type: 'traveler', ...res.data });
    } catch { toast.error('Could not load traveler profile'); }
  };

  const generateBookingInvoice = async (bookingId) => {
    const { cost_per_person_per_day, number_of_people, number_of_days, notes } = invoiceForm;
    if (!cost_per_person_per_day || !number_of_people || !number_of_days) { toast.error('Fill all required fields'); return; }
    try {
      const res = await api.post(`/bookings/${bookingId}/invoice`, {
        cost_per_person_per_day: parseFloat(cost_per_person_per_day),
        number_of_people: parseInt(number_of_people),
        number_of_days: parseInt(number_of_days),
        notes: notes || null
      });
      toast.success(res.data.message);
      setInvoiceDialog(null);
      setInvoiceForm({ cost_per_person_per_day: '', number_of_people: '', number_of_days: '', notes: '' });
      setInvoicePreview(res.data.invoice);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Invoice generation failed'); }
  };

  const viewBookingInvoice = async (bookingId) => {
    try {
      const res = await api.get(`/bookings/${bookingId}/invoice`);
      setInvoicePreview(res.data);
    } catch { toast.error('Could not load invoice'); }
  };

  const markInvoicePaid = async (bookingId) => {
    try {
      const res = await api.post(`/bookings/${bookingId}/invoice/mark-paid`);
      toast.success(res.data.message);
      setInvoicePreview(p => ({ ...p, status: 'paid' }));
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to mark as paid'); }
  };

  const submitReview = async () => {
    if (!reviewDialog) return;
    setReviewSubmitting(true);
    try {
      await api.post('/reviews', { booking_id: reviewDialog.id, rating: reviewRating, comment: reviewComment });
      toast.success('Review submitted!');
      setReviewDialog(null);
      setReviewRating(5);
      setReviewComment('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to submit review'); }
    finally { setReviewSubmitting(false); }
  };


  const printInvoice = () => {
    const el = document.getElementById('invoice-print-area');
    if (!el) return;
    const w = window.open('', '_blank', 'width=800,height=600');
    w.document.write(`<html><head><title>Invoice ${invoicePreview?.invoice_number || ''}</title>
      <style>
        *{margin:0;padding:0;box-sizing:border-box}
        body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;margin:40px 48px;color:#1a1a1a;font-size:13px;line-height:1.5}
        .inv-header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:28px;padding-bottom:16px;border-bottom:3px solid #073763}
        .inv-brand{font-size:22px;font-weight:800;color:#073763;letter-spacing:-0.5px}
        .inv-tagline{font-size:11px;color:#888;margin-top:2px;font-style:italic}
        .inv-number{background:#073763;color:white;padding:5px 14px;border-radius:4px;font-size:12px;font-weight:700;display:inline-block}
        .inv-date{font-size:11px;color:#888;margin-top:6px}
        .inv-status{font-size:11px;font-weight:700;margin-top:4px;text-transform:uppercase;letter-spacing:0.5px}
        .inv-status.paid{color:#059669}.inv-status.unpaid{color:#d97706}
        .inv-parties{display:flex;gap:32px;margin-bottom:24px}
        .inv-party{flex:1}
        .inv-party-label{font-size:10px;text-transform:uppercase;letter-spacing:1.2px;color:#999;font-weight:700;margin-bottom:6px}
        .inv-party-name{font-size:14px;font-weight:700;color:#1a1a1a}
        .inv-party-detail{font-size:12px;color:#555;line-height:1.6}
        .inv-ref{font-size:12px;color:#555;margin-bottom:16px;padding:8px 12px;background:#f8f9fa;border-radius:6px;border-left:3px solid #073763}
        .inv-table{width:100%;border-collapse:collapse;margin:20px 0}
        .inv-table th{background:#073763;color:white;padding:10px 14px;font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:0.5px}
        .inv-table th:first-child{border-radius:6px 0 0 0}.inv-table th:last-child{border-radius:0 6px 0 0}
        .inv-table td{padding:11px 14px;border-bottom:1px solid #eee;font-size:13px;color:#333}
        .inv-table .total-row td{border-top:2px solid #073763;font-weight:700;font-size:15px;background:#f0f4f8;color:#073763}
        .inv-bank{background:#f0f7ff;padding:16px;border-radius:8px;margin:20px 0;border:1px solid #d0e3f7}
        .inv-bank-title{font-size:10px;text-transform:uppercase;letter-spacing:1.2px;color:#073763;font-weight:700;margin-bottom:10px}
        .inv-bank-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px}
        .inv-bank-item{font-size:12px;color:#333}.inv-bank-label{color:#888;font-size:10px;display:block;margin-bottom:2px}
        .inv-notes{background:#f8f8f8;padding:14px 16px;border-radius:8px;margin-top:16px;font-size:12px;color:#555;border-left:3px solid #e5e5e5}
        .inv-footer{margin-top:36px;padding-top:16px;border-top:1px solid #e0e0e0;text-align:center;font-size:11px;color:#999}
        @media print{body{margin:20px 28px}.no-print{display:none}}
      </style>
    </head><body>`);
    w.document.write(el.innerHTML);
    w.document.write('</body></html>');
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 300);
  };

  const startEditProfile = () => {
    setProfileForm({
      company_name: profile?.company_name || '',
      description: profile?.description || '',
      license_number: profile?.license_number || '',
      services: profile?.services || [],
      locations: profile?.locations || [],
      website: profile?.website || '',
      email: profile?.email || '',
      phone_contact: profile?.phone_contact || '',
      address: profile?.address || '',
      years_experience: profile?.years_experience || '',
      languages: profile?.languages || [],
      bank_name: profile?.bank_details?.bank_name || '',
      account_name: profile?.bank_details?.account_name || '',
      account_number: profile?.bank_details?.account_number || '',
      bank_currency: profile?.bank_details?.currency || '',
      swift_code: profile?.bank_details?.swift_code || '',
      branch_code: profile?.bank_details?.branch_code || ''
    });
    setEditingProfile(true);
  };

  const saveProfile = async () => {
    setSubmitting(true);
    try {
      const payload = { ...profileForm };
      if (payload.years_experience) payload.years_experience = parseInt(payload.years_experience);
      else delete payload.years_experience;
      // Bundle bank details
      payload.bank_details = {
        bank_name: payload.bank_name || '',
        account_name: payload.account_name || '',
        account_number: payload.account_number || '',
        currency: payload.bank_currency || '',
        swift_code: payload.swift_code || '',
        branch_code: payload.branch_code || ''
      };
      delete payload.bank_name; delete payload.account_name; delete payload.account_number;
      delete payload.bank_currency; delete payload.swift_code; delete payload.branch_code;
      await api.put('/operator/profile', payload);
      toast.success('Profile updated');
      setEditingProfile(false);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    finally { setSubmitting(false); }
  };

  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingAvatar(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await api.post('/profile/avatar', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Photo updated');
      fetchAll();
    } catch { toast.error('Upload failed'); }
    finally { setUploadingAvatar(false); }
  };

  const uploadGalleryPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingGallery(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await api.post('/operator/gallery', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Photo added to gallery');
      fetchAll();
    } catch { toast.error('Upload failed'); }
    finally { setUploadingGallery(false); }
  };

  const deleteGalleryPhoto = async (photoId) => {
    try {
      await api.delete(`/operator/gallery/${photoId}`);
      toast.success('Photo removed');
      fetchAll();
    } catch { toast.error('Failed to delete'); }
  };

  const addTeamMember = async () => {
    if (!teamForm.name || !teamForm.role) return;
    try {
      await api.post('/operator/team', teamForm);
      toast.success('Team member added');
      setTeamForm({ name: '', role: '', bio: '' });
      setShowTeamDialog(false);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add'); }
  };

  const removeTeamMember = async (memberId) => {
    try {
      await api.delete(`/operator/team/${memberId}`);
      toast.success('Team member removed');
      fetchAll();
    } catch { toast.error('Failed to remove'); }
  };

  const createTeamAccount = async () => {
    if (!teamAccountForm.name || !teamAccountForm.phone || !teamAccountForm.pin) return;
    setCreatingTeamAccount(true);
    try {
      await api.post('/operator/team/accounts', teamAccountForm);
      toast.success(`Account created for ${teamAccountForm.name}`);
      setTeamAccountForm({ name: '', phone: '', pin: '1234', team_roles: ['leads_agent'] });
      setShowTeamAccountDialog(false);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create account'); }
    finally { setCreatingTeamAccount(false); }
  };

  const deleteTeamAccount = async (accountId) => {
    try {
      await api.delete(`/operator/team/accounts/${accountId}`);
      toast.success('Team account removed');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to remove'); }
  };

  const toggleTeamRole = (role) => {
    setTeamAccountForm(prev => {
      const roles = prev.team_roles.includes(role)
        ? prev.team_roles.filter(r => r !== role)
        : [...prev.team_roles, role];
      return { ...prev, team_roles: roles.length > 0 ? roles : [role] };
    });
  };

  const topupWallet = async () => {
    const credits = parseInt(topupCredits);
    if (!credits || credits <= 0) { toast.error('Enter a valid number of credits'); return; }
    setSubmitting(true);
    try {
      const res = await api.post('/operator/wallet/topup', { credits });
      setGeneratedInvoice(res.data.invoice);
      toast.success(res.data.message);
      setTopupCredits('');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Request failed'); }
    finally { setSubmitting(false); }
  };

  const unlockLead = async (tripId) => {
    setUnlocking(tripId);
    try {
      const res = await api.post(`/operator/leads/${tripId}/unlock`);
      if (res.data.already_unlocked) {
        toast.info('Lead already unlocked');
      } else {
        toast.success(res.data.message);
      }
      fetchAll();
    } catch (e) {
      const detail = e.response?.data?.detail || 'Failed to unlock';
      if (e.response?.status === 402) {
        toast.error('Insufficient credits. Please buy more credits.');
        setShowTopupDialog(true);
      } else { toast.error(detail); }
    }
    finally { setUnlocking(null); }
  };

  const promoteItineraryAction = async () => {
    if (!promoteItinerary) return;
    setSubmitting(true);
    try {
      const res = await api.post('/operator/promote', {
        itinerary_id: promoteItinerary.id,
        duration_days: promoDuration
      });
      toast.success(res.data.message);
      setShowPromoteDialog(false);
      setPromoteItinerary(null);
      fetchAll();
    } catch (e) {
      const detail = e.response?.data?.detail || 'Failed to promote';
      if (e.response?.status === 402) {
        toast.error('Insufficient credits. Please buy more credits.');
        setShowTopupDialog(true);
      } else { toast.error(detail); }
    }
    finally { setSubmitting(false); }
  };

  const isLeadUnlocked = (tripId) => unlockedLeads.includes(tripId);
  const isLeadRejected = (tripId) => rejectedLeads.includes(tripId);

  const rejectLead = async (tripId) => {
    try {
      await api.post(`/operator/leads/${tripId}/reject`);
      toast.success('Lead rejected');
      setRejectedLeads(prev => [...prev, tripId]);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to reject'); }
  };

  const getLeadExpiry = (createdAt) => {
    const created = new Date(createdAt);
    const expiry = new Date(created.getTime() + 7 * 24 * 60 * 60 * 1000);
    const now = new Date();
    const diff = expiry - now;
    if (diff <= 0) return { label: 'Expired', color: 'text-red-600' };
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    if (days > 2) return { label: `${days}d`, color: 'text-zinc-500' };
    if (days >= 1) return { label: `${days}d ${hours}h`, color: 'text-amber-600' };
    return { label: `${hours}h`, color: 'text-red-600' };
  };

  const openItineraryBuilder = (trip) => {
    navigate(`/operator/create-itinerary/${trip.id}`);
  };

  const findConversationForTrip = (tripId) => {
    return conversations.find(c => c.trip_id === tripId);
  };

  const openMessageForLead = (tripId) => {
    const convo = findConversationForTrip(tripId);
    if (convo) {
      openOpConversation(convo);
    } else {
      toast.error('No conversation found for this lead. Submit a proposal first.');
    }
  };

  const openProposalView = (trip) => {
    const proposal = myItineraries.find(it => it.trip_id === trip.id);
    if (proposal) {
      setPreviewProposal(proposal);
      setPreviewTab('proposal');
    }
  };

  const deleteItinerary = async (id) => {
    try {
      await api.delete(`/itineraries/${id}`);
      toast.success('Itinerary deleted');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete'); }
  };

  const publishItinerary = async (id) => {
    try {
      await api.post(`/operator/itineraries/${id}/publish`);
      toast.success('Itinerary published! Monthly hosting fee will apply.');
      fetchAll();
    } catch (e) {
      const detail = e.response?.data?.detail || 'Failed to publish';
      if (e.response?.status === 402) {
        toast.error(detail);
        setShowTopupDialog(true);
      } else {
        toast.error(detail);
      }
    }
  };

  const unpublishItinerary = async (id) => {
    try {
      await api.post(`/operator/itineraries/${id}/unpublish`);
      toast.success('Itinerary unpublished. Hosting fee stopped.');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to unpublish'); }
  };

  const requestFeaturing = async (id) => {
    try {
      await api.post(`/operator/itineraries/${id}/request-featuring`);
      toast.success('Feature request submitted. Admin will review your itinerary.');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to request featuring'); }
  };

  // ====== OPERATOR MESSAGING ======
  const openOpConversation = async (convo) => {
    setActiveConversation(convo);
    setActiveView('chat');
    try {
      const res = await api.get(`/conversations/${convo.id}/messages`);
      setOpMessages(res.data);
      await api.post(`/conversations/${convo.id}/read`);
      fetchAll();
    } catch { toast.error('Failed to load messages'); }
    if (opWsRef.current) opWsRef.current.close();
    const token = localStorage.getItem('karibu_token');
    const wsUrl = API_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    const ws = new WebSocket(`${wsUrl}/ws/chat/${convo.id}?token=${token}`);
    ws.onmessage = (event) => {
      const msg = JSON.parse(event.data);
      setOpMessages(prev => prev.find(m => m.id === msg.id) ? prev : [...prev, msg]);
    };
    opWsRef.current = ws;
  };

  useEffect(() => {
    if (opMsgEndRef.current) opMsgEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [opMessages]);

  useEffect(() => { return () => { if (opWsRef.current) opWsRef.current.close(); }; }, []);

  const sendOpMessage = async () => {
    if (!opNewMessage.trim() || !activeConversation) return;
    setSendingMsg(true);
    try {
      if (opWsRef.current?.readyState === WebSocket.OPEN) {
        opWsRef.current.send(JSON.stringify({ content: opNewMessage.trim() }));
      } else {
        await api.post(`/conversations/${activeConversation.id}/messages`, { content: opNewMessage.trim() });
        const res = await api.get(`/conversations/${activeConversation.id}/messages`);
        setOpMessages(res.data);
      }
      setOpNewMessage('');
    } catch { toast.error('Failed to send'); }
    finally { setSendingMsg(false); }
  };

  const formatTime = (d) => { try { return format(new Date(d), 'h:mm a'); } catch { return ''; } };

  const getAvatarUrl = () => {
    if (!profile?.avatar_path) return null;
    const token = localStorage.getItem('karibu_token');
    return `${API_URL}/api/files/${profile.avatar_path}?auth=${token}`;
  };

  const getGalleryUrl = (path) => {
    const token = localStorage.getItem('karibu_token');
    return `${API_URL}/api/files/${path}?auth=${token}`;
  };

  const handleLogout = () => { logout(); navigate('/'); };
  const formatDate = (d) => { try { return format(new Date(d), 'MMM dd, yyyy'); } catch { return d || ''; } };

  const toggleService = (service) => {
    setProfileForm(prev => ({
      ...prev,
      services: prev.services.includes(service)
        ? prev.services.filter(s => s !== service)
        : [...prev.services, service]
    }));
  };

  const toggleLocation = (loc) => {
    setProfileForm(prev => ({
      ...prev,
      locations: prev.locations.includes(loc)
        ? prev.locations.filter(l => l !== loc)
        : [...prev.locations, loc]
    }));
  };

  const toggleLanguage = (lang) => {
    setProfileForm(prev => ({
      ...prev,
      languages: prev.languages.includes(lang)
        ? prev.languages.filter(l => l !== lang)
        : [...prev.languages, lang]
    }));
  };

  if (loading) return (
    <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center">
      <div className="spinner" />
    </div>
  );

  // Pending Approval State
  if (!isApproved) {
    return (
      <div className="min-h-screen bg-[#FAFAF9]">
        <header className="bg-white border-b border-neutral-100">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center" data-testid="brand-link"><img src="/safarinodes-logo-dark.png" alt="safarinodes" className="h-8 w-auto" /></Link>
            <button onClick={handleLogout} className="p-2 hover:bg-red-50 rounded-full text-neutral-400 hover:text-red-500 transition" data-testid="logout-btn">
              <LogOut size={18} />
            </button>
          </div>
        </header>
        <main className="max-w-lg mx-auto px-6 py-20 text-center">
          <div className="w-20 h-20 rounded-2xl bg-amber-50 flex items-center justify-center mx-auto mb-6">
            <Clock className="text-amber-500" size={36} />
          </div>
          <h1 className="text-2xl font-bold text-neutral-900 mb-3" data-testid="pending-title">Application Under Review</h1>
          <p className="text-neutral-500 mb-8 max-w-sm mx-auto">
            Your operator account is being reviewed by our team. You'll be notified once approved and can start receiving trip requests.
          </p>
          <div className="bg-white rounded-xl border border-neutral-200 p-5 text-left space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-neutral-500">Name</span>
              <span className="font-medium text-neutral-900">{user?.name}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-neutral-500">Company</span>
              <span className="font-medium text-neutral-900">{profile?.company_name || 'N/A'}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-neutral-500">Status</span>
              <Badge className="bg-amber-50 text-amber-700 border border-amber-200">Pending Review</Badge>
            </div>
          </div>
        </main>
      </div>
    );
  }

  const getDuration = (trip) => {
    if (!trip.travel_dates?.start || !trip.travel_dates?.end) return 0;
    return Math.ceil((new Date(trip.travel_dates.end) - new Date(trip.travel_dates.start)) / (1000 * 60 * 60 * 24)) + 1;
  };

  const filteredTrips = trips.filter(trip => {
    // Search filter
    if (leadSearch) {
      const q = leadSearch.toLowerCase();
      const name = (trip.traveler_name || '').toLowerCase();
      const summary = (trip.broadcast_summary || '').toLowerCase();
      const id = (trip.id || '').toLowerCase();
      const nat = (trip.traveler_nationality || '').toLowerCase();
      if (!name.includes(q) && !summary.includes(q) && !id.includes(q) && !nat.includes(q)) return false;
    }
    if (tripFilter === 'all') return true;
    if (tripFilter === 'budget') return (trip.budget_range?.max || 0) <= 1500;
    if (tripFilter === 'mid') return (trip.budget_range?.min || 0) >= 1500 && (trip.budget_range?.max || 0) <= 3500;
    if (tripFilter === 'luxury') return (trip.budget_range?.min || 0) >= 3500;
    if (tripFilter === 'solo') return trip.group_size === 1;
    if (tripFilter === 'couple') return trip.group_size === 2;
    if (tripFilter === 'group') return trip.group_size >= 3;
    if (tripFilter === 'short') return getDuration(trip) <= 5;
    if (tripFilter === 'week') return getDuration(trip) >= 6 && getDuration(trip) <= 10;
    if (tripFilter === 'long') return getDuration(trip) > 10;
    return true;
  }).sort((a, b) => {
    if (tripSort === 'newest') return new Date(b.created_at) - new Date(a.created_at);
    if (tripSort === 'oldest') return new Date(a.created_at) - new Date(b.created_at);
    if (tripSort === 'budget-high') return (b.budget_range?.max || 0) - (a.budget_range?.max || 0);
    if (tripSort === 'budget-low') return (a.budget_range?.min || 0) - (b.budget_range?.min || 0);
    if (tripSort === 'group-large') return (b.group_size || 0) - (a.group_size || 0);
    if (tripSort === 'duration-long') return getDuration(b) - getDuration(a);
    return 0;
  });

  const totalLeadPages = Math.ceil(filteredTrips.length / leadsPerPage);
  const paginatedTrips = filteredTrips.slice((leadsPage - 1) * leadsPerPage, leadsPage * leadsPerPage);

  const NAV = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'inbox', label: 'Leads', icon: Inbox, count: trips.length },
    { id: 'messages', label: 'Messaging', icon: MessageCircle, count: opUnreadCount },
    { id: 'itineraries', label: 'Curated Itineraries', icon: Globe, count: myItineraries.filter(it => it.is_published && it.hosting_active).length },
    { id: 'bookings', label: 'Bookings', icon: BookOpen, count: confirmedBookings.length || null },
    { id: 'team_accounts', label: 'Team Accounts', icon: Users, count: teamAccounts.length },
    { id: 'wallet', label: 'Wallet', icon: Wallet },
    { id: 'profile', label: 'Company Profile', icon: Building2 },
  ];

  return (
    <div className="min-h-screen bg-white flex" data-testid="operator-dashboard">
      {/* Sidebar - Desktop */}
      <aside className={`hidden lg:flex flex-col ${sidebarCollapsed ? 'w-16' : 'w-52'} bg-zinc-900 fixed h-full z-30 transition-all duration-200`} data-testid="sidebar">
        <div className={`${sidebarCollapsed ? 'px-2 pt-5 pb-3' : 'px-5 pt-6 pb-4'} flex items-center justify-between`}>
          {!sidebarCollapsed && (
            <div>
              <Link to="/" className="flex items-center" data-testid="brand-link"><img src="/safarinodes-logo.png" alt="safarinodes" className="h-7 w-auto" /></Link>
              <p className="text-[10px] text-zinc-500 font-medium uppercase tracking-widest mt-0.5">Operator</p>
            </div>
          )}
          <button onClick={() => setSidebarCollapsed(!sidebarCollapsed)} className="p-1.5 rounded-lg text-zinc-400 hover:text-white hover:bg-white/10 transition" data-testid="sidebar-toggle">
            {sidebarCollapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          </button>
        </div>

        <nav className={`flex-1 ${sidebarCollapsed ? 'px-1.5' : 'px-3'} space-y-0.5`}>
          {NAV.map(item => {
            const active = activeView === item.id || (activeView === 'chat' && item.id === 'messages');
            return (
              <button
                key={item.id}
                onClick={() => setActiveView(item.id)}
                title={sidebarCollapsed ? item.label : undefined}
                className={`w-full flex items-center ${sidebarCollapsed ? 'justify-center px-2' : 'gap-2.5 px-3'} py-2.5 rounded-lg text-sm font-medium transition-all ${
                  active
                    ? 'bg-white/15 text-white'
                    : 'text-zinc-300 hover:text-white hover:bg-white/5'
                }`}
                data-testid={`nav-${item.id}`}
              >
                <item.icon size={16} strokeWidth={1.8} />
                {!sidebarCollapsed && <span className="flex-1 text-left">{item.label}</span>}
                {!sidebarCollapsed && item.count > 0 && (
                  <span className="min-w-[18px] h-[18px] px-1 bg-zinc-700 text-zinc-300 text-[10px] font-bold rounded flex items-center justify-center">
                    {item.count}
                  </span>
                )}
                {sidebarCollapsed && item.count > 0 && (
                  <span className="absolute -top-1 -right-1 w-2 h-2 bg-amber-400 rounded-full" />
                )}
              </button>
            );
          })}
        </nav>

        {/* Credit Balance */}
        <div className={`${sidebarCollapsed ? 'px-1.5' : 'px-3'} py-3`}>
          <div className={`bg-white/5 rounded-lg ${sidebarCollapsed ? 'px-2 py-2' : 'px-3 py-2.5'}`}>
            <div className={`flex items-center ${sidebarCollapsed ? 'justify-center' : 'gap-2'}`}>
              <Wallet size={13} className="text-zinc-400" />
              {!sidebarCollapsed && (
                <>
                  <span className="text-xs font-semibold text-zinc-200">{Math.floor(wallet?.balance || 0).toLocaleString()}</span>
                  <span className="text-[10px] text-zinc-400">credits</span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* User + Logout */}
        <div className={`${sidebarCollapsed ? 'px-1.5' : 'px-3'} pb-4 border-t border-zinc-800 pt-3`}>
          {!sidebarCollapsed && (
            <div className="flex items-center gap-2.5 px-3 py-2">
              {getAvatarUrl() ? (
                <img src={getAvatarUrl()} alt="" className="w-7 h-7 rounded-full object-cover" />
              ) : (
                <div className="w-7 h-7 rounded-full bg-zinc-700 flex items-center justify-center text-zinc-300 text-[10px] font-bold">
                  {(profile?.company_name || user?.name || 'O').charAt(0).toUpperCase()}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-zinc-100 truncate">{profile?.company_name || user?.name}</p>
                <p className="text-[10px] text-zinc-400 truncate">{user?.phone ? `+${user.phone}` : ''}</p>
              </div>
            </div>
          )}
          <button onClick={handleLogout} className={`w-full flex items-center ${sidebarCollapsed ? 'justify-center px-2' : 'gap-2.5 px-3'} py-2 rounded-lg text-xs text-zinc-500 hover:text-red-400 hover:bg-white/5 transition`} data-testid="sidebar-logout" title={sidebarCollapsed ? 'Sign Out' : undefined}>
            <LogOut size={14} /> {!sidebarCollapsed && 'Sign Out'}
          </button>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-zinc-900">
        <div className="px-4 py-3 flex items-center justify-between">
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="p-1.5 hover:bg-white/10 rounded-lg text-zinc-400">
            <Menu size={18} />
          </button>
          <Link to="/" className="flex items-center"><img src="/safarinodes-logo.png" alt="safarinodes" className="h-7 w-auto" /></Link>
          <button onClick={handleLogout} className="p-1.5 hover:bg-white/10 rounded-lg text-zinc-500">
            <LogOut size={16} />
          </button>
        </div>
        {mobileMenuOpen && (
          <div className="border-t border-zinc-800 bg-zinc-900 px-4 py-3 flex gap-2 overflow-x-auto no-scrollbar">
            {NAV.map(item => (
              <button
                key={item.id}
                onClick={() => { setActiveView(item.id); setMobileMenuOpen(false); }}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap transition ${
                  activeView === item.id ? 'bg-white/10 text-white' : 'text-zinc-400'
                }`}
              >
                <item.icon size={14} /> {item.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className={`flex-1 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-52'} pt-14 lg:pt-0 transition-all duration-200`}>
        <main className="p-5 lg:p-6 w-full">

          {/* ===== DASHBOARD ===== */}
          {activeView === 'dashboard' && (
            <div data-testid="dashboard-view">
              {/* Header row */}
              <div className="flex items-center justify-between mb-5">
                <div>
                  <h1 className="text-lg font-semibold text-zinc-900" data-testid="dashboard-title">
                    {profile?.company_name || user?.name}
                  </h1>
                  <p className="text-sm text-zinc-500 mt-0.5">Overview</p>
                </div>
                <Button onClick={() => setShowTopupDialog(true)} className="bg-zinc-900 hover:bg-zinc-800 text-white rounded-lg px-4 py-2 text-xs font-medium" data-testid="topup-btn-dashboard">
                  <Plus size={13} className="mr-1" /> Top Up Credits
                </Button>
              </div>

              {/* Stats Strip */}
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-5" data-testid="stats-grid">
                {[
                  { label: 'Leads', value: stats?.total_requests || 0 },
                  { label: 'Proposals', value: stats?.total_itineraries || 0 },
                  { label: 'Won', value: stats?.selected_itineraries || 0 },
                  { label: 'Conversion', value: `${stats?.conversion_rate || 0}%` },
                  { label: 'Revenue', value: `$${(stats?.total_revenue || 0).toLocaleString()}` },
                ].map((s, i) => (
                  <div key={i} className="rounded-lg border border-zinc-200 px-4 py-3" data-testid={`stat-card-${i}`}>
                    <p className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold">{s.label}</p>
                    <p className="text-xl font-bold text-zinc-900 mt-0.5">{s.value}</p>
                  </div>
                ))}
              </div>

              {/* Balance card */}
              <div className="bg-zinc-900 rounded-lg p-4 sm:p-5 text-white mb-5" data-testid="wallet-card">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                  <div>
                    <p className="text-[11px] uppercase tracking-wider text-zinc-400 font-semibold">Credit Balance</p>
                    <p className="text-2xl font-bold mt-1">{Math.floor(wallet?.balance || 0).toLocaleString()}</p>
                  </div>
                  <div className="flex gap-4 sm:gap-5 text-xs sm:text-sm text-zinc-300 flex-wrap">
                    <span>{unlockedLeads.length} unlocked</span>
                    <span>{wallet?.active_promotions || 0} promos</span>
                    <span>{wallet?.published_itineraries || 0} published</span>
                  </div>
                </div>
              </div>

              {/* Recent proposals */}
              {myItineraries.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h2 className="text-sm font-bold text-zinc-900">Recent proposals</h2>
                    <button onClick={() => setActiveView('itineraries')} className="text-xs text-zinc-500 hover:text-zinc-900 transition font-medium">
                      View all <ArrowUpRight size={11} className="inline" />
                    </button>
                  </div>
                  <div className="rounded-lg border border-zinc-200 divide-y divide-zinc-100">
                    {myItineraries.slice(0, 5).map(it => (
                      <div key={it.id} className="px-4 py-3 flex items-center justify-between" data-testid={`recent-itin-${it.id}`}>
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-zinc-900 text-sm truncate">{it.title}</p>
                          <p className="text-xs text-zinc-500 mt-0.5">{it.duration_days}d &middot; ${it.total_price}</p>
                        </div>
                        <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded ${
                          it.status === 'selected' ? 'bg-zinc-900 text-white' : it.status === 'submitted' ? 'bg-zinc-100 text-zinc-600' : 'bg-zinc-50 text-zinc-400'
                        }`}>
                          {it.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ===== INBOX - Leads Table ===== */}
          {activeView === 'inbox' && !previewTrip && (
            <div data-testid="inbox-view">
              <div className="mb-4">
                <h1 className="text-lg font-semibold text-zinc-900">Leads</h1>
              </div>

              {/* Leads Analytics */}
              {trips.length > 0 && (() => {
                const total = trips.length;
                const unlocked = trips.filter(t => unlockedLeads.includes(t.id)).length;
                const locked = total - unlocked;
                const curated = trips.filter(t => t.lead_type === 'curated').length;
                const responded = trips.filter(t => myItineraries.some(it => it.trip_id === t.id)).length;
                const newLeads = trips.filter(t => {
                  const age = (Date.now() - new Date(t.created_at).getTime()) / 86400000;
                  return age <= 1;
                }).length;
                return (
                  <div className="bg-zinc-900 rounded-2xl px-6 py-5 mb-6" data-testid="leads-analytics">
                    <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                    {[
                      { label: 'Total', value: total, color: 'text-white' },
                      { label: 'New (24h)', value: newLeads, color: 'text-emerald-400' },
                      { label: 'Unlocked', value: unlocked, color: 'text-amber-400' },
                      { label: 'Locked', value: locked, color: 'text-zinc-400' },
                      { label: 'Curated', value: curated, color: 'text-amber-300' },
                      { label: 'Responded', value: responded, color: 'text-blue-400' },
                    ].map(stat => (
                      <div key={stat.label} className="text-center">
                        <p className={`text-xl font-bold ${stat.color}`}>{stat.value}</p>
                        <p className="text-[10px] text-zinc-400 font-medium uppercase tracking-wide mt-0.5">{stat.label}</p>
                      </div>
                    ))}
                    </div>
                  </div>
                );
              })()}

              {trips.length === 0 ? (
                <div className="text-center py-20">
                  <Inbox size={40} className="mx-auto mb-3 text-zinc-200" strokeWidth={1.2} />
                  <h3 className="font-semibold text-zinc-900 mb-1 text-sm">No leads yet</h3>
                  <p className="text-xs text-zinc-400 max-w-sm mx-auto">Trip requests matching your services will appear here.</p>
                </div>
              ) : filteredTrips.length === 0 ? (
                <div className="text-center py-16">
                  <p className="text-sm text-zinc-500">No leads match "{leadSearch}"</p>
                  <button onClick={() => setLeadSearch('')} className="text-sm text-zinc-700 underline mt-2" data-testid="clear-search-empty">Clear search</button>
                </div>
              ) : (
                <div data-testid="leads-table">
                  {/* Search & Sort */}
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 mb-4">
                    <div className="relative flex-1 sm:flex-none" data-testid="lead-search">
                      <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                      <input
                        type="text"
                        placeholder="Search by name, ID, nationality..."
                        value={leadSearch}
                        onChange={e => { setLeadSearch(e.target.value); setLeadsPage(1); }}
                        className="pl-9 pr-3 py-2 w-full sm:w-[280px] text-sm border border-zinc-200 rounded-lg bg-white text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:border-zinc-400 focus:ring-1 focus:ring-zinc-200 transition"
                        data-testid="lead-search-input"
                      />
                      {leadSearch && (
                        <button onClick={() => setLeadSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700" data-testid="clear-search">
                          <X size={14} />
                        </button>
                      )}
                    </div>
                    <select
                      value={tripSort}
                      onChange={(e) => { setTripSort(e.target.value); setLeadsPage(1); }}
                      className="text-sm bg-white border border-zinc-200 rounded-lg px-3 py-2 text-zinc-700 focus:outline-none focus:ring-1 focus:ring-zinc-200"
                      data-testid="trip-sort-select"
                    >
                      <option value="newest">Newest</option>
                      <option value="oldest">Oldest</option>
                      <option value="budget-high">Budget high</option>
                      <option value="budget-low">Budget low</option>
                    </select>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                    {paginatedTrips.map((trip) => {
                      const unlocked = isLeadUnlocked(trip.id);
                      const rejected = isLeadRejected(trip.id);
                      const hasResponded = myItineraries.some(it => it.trip_id === trip.id);
                      const initial = unlocked ? (trip.traveler_name?.charAt(0) || '?') : '?';
                      const isCurated = trip.lead_type === 'curated';
                      const expiry = getLeadExpiry(trip.created_at);
                      const statusLabel = rejected ? 'Rejected' : hasResponded ? 'Responded' : unlocked ? 'Open' : 'Locked';
                      const statusColor = rejected ? 'bg-red-400' : hasResponded ? 'bg-emerald-500' : unlocked ? 'bg-amber-400' : 'bg-zinc-300';
                      const statusText = rejected ? 'text-red-500' : hasResponded ? 'text-emerald-700' : unlocked ? 'text-amber-700' : 'text-zinc-400';

                      return (
                        <div
                          key={trip.id}
                          className={`bg-white rounded-xl border border-zinc-200 p-4 hover:border-zinc-300 transition-all flex gap-4 ${rejected ? 'opacity-50' : ''}`}
                          data-testid={`trip-request-${trip.id}`}
                        >
                          {/* Left: Large Avatar */}
                          <div className={`w-14 h-14 rounded-2xl flex items-center justify-center text-lg font-bold shrink-0 ${
                            unlocked ? 'bg-zinc-900 text-white' : 'bg-zinc-200 text-zinc-400'
                          }`}>
                            {initial.toUpperCase()}
                          </div>

                          {/* Right: Content */}
                          <div className="flex-1 min-w-0 flex flex-col">
                            {/* Name + Meta */}
                            <div className="flex items-start justify-between">
                              <div className="min-w-0">
                                <p className="text-sm font-semibold text-zinc-900 truncate">{unlocked ? trip.traveler_name : 'Locked Lead'}</p>
                                <div className="flex items-center gap-1.5 text-[10px] text-zinc-400 mt-0.5">
                                  <span className="font-mono">#{trip.id.slice(0, 6)}</span>
                                  {unlocked && trip.traveler_nationality && (
                                    <><span className="text-zinc-300">|</span><span>{trip.traveler_nationality}</span></>
                                  )}
                                </div>
                              </div>
                              <span className={`text-[10px] font-medium shrink-0 ml-2 ${expiry.color}`}>{expiry.label}</span>
                            </div>

                            {/* Badges Row */}
                            <div className="flex items-center gap-2 mt-2">
                              <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${isCurated ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>
                                {isCurated ? 'Curated' : 'Custom'}
                              </span>
                              <div className="flex items-center gap-1">
                                <span className={`w-1.5 h-1.5 rounded-full ${statusColor}`} />
                                <span className={`text-[10px] font-medium ${statusText}`}>{statusLabel}</span>
                              </div>
                            </div>

                            {/* Summary */}
                            <p className="text-[12px] text-zinc-500 mt-1.5 leading-relaxed line-clamp-2 flex-1">
                              {trip.broadcast_summary || trip.destination_interests?.join(', ') || '—'}
                            </p>

                            {/* Meta Row */}
                            {unlocked && (
                              <div className="flex items-center gap-3 mt-2 pt-2 border-t border-zinc-100 text-[10px] text-zinc-400 flex-wrap">
                                {trip.group_size && <span className="flex items-center gap-0.5"><Users size={10} /> {trip.group_size}</span>}
                                {trip.budget_range && <span className="flex items-center gap-0.5"><DollarSign size={10} /> ${trip.budget_range.min?.toLocaleString()}–${trip.budget_range.max?.toLocaleString()}</span>}
                                {trip.travel_dates?.start && <span className="flex items-center gap-0.5"><Calendar size={10} /> {new Date(trip.travel_dates.start).toLocaleDateString('en-US', {month: 'short', day: 'numeric'})}</span>}
                              </div>
                            )}

                            {/* Actions */}
                            <div className="mt-2 pt-2 border-t border-zinc-100 flex items-center justify-end gap-2">
                              {rejected ? (
                                <span className="text-xs text-zinc-400">Dismissed</span>
                              ) : unlocked ? (
                                <>
                                  {hasResponded && findConversationForTrip(trip.id) && (
                                    <button
                                      onClick={(e) => { e.stopPropagation(); openMessageForLead(trip.id); }}
                                      className="p-1.5 rounded-lg text-zinc-400 hover:text-zinc-900 hover:bg-zinc-100 transition"
                                      title="Message traveler"
                                      data-testid={`msg-btn-${trip.id}`}
                                    >
                                      <MessageCircle size={14} />
                                    </button>
                                  )}
                                  <button
                                    onClick={(e) => { e.stopPropagation(); previewTraveler(trip.traveler_id); }}
                                    className="p-1.5 rounded-lg text-zinc-400 hover:text-blue-600 hover:bg-blue-50 transition"
                                    title="View Traveler Profile"
                                    data-testid={`view-traveler-${trip.id}`}
                                  >
                                    <User size={14} />
                                  </button>
                                  <button
                                    onClick={() => { setPreviewTrip(trip); setPreviewTab('details'); setPreviewProposal(null); }}
                                    className="inline-flex items-center gap-1 text-xs font-semibold text-zinc-900 hover:text-zinc-600 transition"
                                    data-testid={`preview-btn-${trip.id}`}
                                  >
                                    Preview <ArrowUpRight size={12} />
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    onClick={() => rejectLead(trip.id)}
                                    className="text-[11px] text-zinc-400 hover:text-red-500 transition font-medium"
                                    data-testid={`reject-btn-${trip.id}`}
                                  >
                                    Reject
                                  </button>
                                  <Button
                                    onClick={() => unlockLead(trip.id)}
                                    disabled={unlocking === trip.id}
                                    size="sm"
                                    className="bg-zinc-900 text-white hover:bg-zinc-800 rounded-lg px-3 py-1.5 text-[11px] font-semibold h-auto"
                                    data-testid={`unlock-btn-${trip.id}`}
                                  >
                                    {unlocking === trip.id ? <Loader2 size={12} className="animate-spin" /> : <><Unlock size={11} className="mr-1" /> Unlock</>}
                                  </Button>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Pagination */}
                  <div className="flex items-center justify-between pt-4 border-t border-zinc-200 mt-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-zinc-500">Rows per page:</span>
                      <select
                        value={leadsPerPage}
                        onChange={e => { setLeadsPerPage(Number(e.target.value)); setLeadsPage(1); }}
                        className="text-xs border border-zinc-200 rounded-md px-2 py-1 text-zinc-700 bg-white focus:outline-none"
                        data-testid="leads-per-page"
                      >
                        <option value={9}>9</option>
                        <option value={18}>18</option>
                        <option value={36}>36</option>
                        <option value={45}>45</option>
                      </select>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-zinc-500">
                        {Math.min((leadsPage - 1) * leadsPerPage + 1, filteredTrips.length)}–{Math.min(leadsPage * leadsPerPage, filteredTrips.length)} of {filteredTrips.length}
                      </span>
                      <div className="flex gap-1">
                        <button
                          onClick={() => setLeadsPage(p => Math.max(1, p - 1))}
                          disabled={leadsPage === 1}
                          className="p-1.5 rounded-md border border-zinc-200 text-zinc-500 hover:bg-zinc-50 disabled:opacity-30 disabled:cursor-default transition"
                          data-testid="leads-prev-page"
                        >
                          <ChevronLeft size={14} />
                        </button>
                        <button
                          onClick={() => setLeadsPage(p => Math.min(Math.ceil(filteredTrips.length / leadsPerPage), p + 1))}
                          disabled={leadsPage >= Math.ceil(filteredTrips.length / leadsPerPage)}
                          className="p-1.5 rounded-md border border-zinc-200 text-zinc-500 hover:bg-zinc-50 disabled:opacity-30 disabled:cursor-default transition"
                          data-testid="leads-next-page"
                        >
                          <ChevronRight size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ===== LEAD PREVIEW ===== */}
          {activeView === 'inbox' && previewTrip && (
            <div data-testid="lead-preview">
              {/* Back button */}
              <button onClick={() => setPreviewTrip(null)} className="flex items-center gap-1.5 text-sm text-zinc-400 hover:text-zinc-900 transition mb-6" data-testid="preview-back">
                <ChevronLeft size={16} /> Back to Leads
              </button>

              {/* Preview Header */}
              <div className="bg-zinc-900 rounded-2xl p-6 mb-6" data-testid="preview-header">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-2xl bg-white/15 text-white flex items-center justify-center text-xl font-bold">
                      {(previewTrip.traveler_name?.charAt(0) || '?').toUpperCase()}
                    </div>
                    <div>
                      <h1 className="text-xl font-bold text-white">{previewTrip.traveler_name}</h1>
                      <div className="flex items-center gap-2 mt-1.5 text-sm text-zinc-400">
                        <span className="font-mono text-xs bg-white/10 text-zinc-300 px-2 py-0.5 rounded">#{previewTrip.id.slice(0, 8)}</span>
                        {previewTrip.traveler_nationality && <span className="text-zinc-300">{previewTrip.traveler_nationality}</span>}
                        <span className={`px-2 py-0.5 rounded text-xs font-semibold ${getLeadExpiry(previewTrip.created_at).color === 'text-red-600' ? 'bg-red-500/20 text-red-300' : getLeadExpiry(previewTrip.created_at).color === 'text-amber-600' ? 'bg-amber-500/20 text-amber-300' : 'bg-white/10 text-zinc-300'}`}>
                          {getLeadExpiry(previewTrip.created_at).label}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {findConversationForTrip(previewTrip.id) && (
                      <Button onClick={() => openMessageForLead(previewTrip.id)} variant="outline" className="bg-transparent border-white/20 text-white hover:bg-white/10 rounded-xl px-4 py-2.5 text-sm font-semibold" data-testid="preview-message-btn">
                        <MessageCircle size={15} className="mr-2" /> Message
                      </Button>
                    )}
                    {myItineraries.some(it => it.trip_id === previewTrip.id) ? (
                      <Button onClick={() => openProposalView(previewTrip)} className="bg-white text-zinc-900 hover:bg-zinc-100 rounded-xl px-5 py-2.5 text-sm font-semibold" data-testid="view-proposal-btn">
                        <Eye size={15} className="mr-2" /> View Proposal
                      </Button>
                    ) : (
                      <Button onClick={() => { openItineraryBuilder(previewTrip); setPreviewTrip(null); }} className="bg-white text-zinc-900 hover:bg-zinc-100 rounded-xl px-5 py-2.5 text-sm font-semibold" data-testid="create-proposal-btn">
                        <Plus size={15} className="mr-2" /> Create Proposal
                      </Button>
                    )}
                  </div>
                </div>

                {/* Summary */}
                <div className="mt-5 pt-4 border-t border-white/10">
                  <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mb-1.5">Trip Summary</p>
                  <p className="text-[15px] text-zinc-200 leading-relaxed">
                    {previewTrip.broadcast_summary || `${previewTrip.destination_interests?.join(', ') || 'Trip'} for ${previewTrip.group_size} traveler${previewTrip.group_size !== 1 ? 's' : ''}, $${previewTrip.budget_range?.min?.toLocaleString()}–$${previewTrip.budget_range?.max?.toLocaleString()} budget`}
                  </p>
                </div>
              </div>

              {/* Tab Navigation (Details vs Proposal) */}
              {myItineraries.some(it => it.trip_id === previewTrip.id) && (
                <div className="flex gap-1 mb-6 bg-zinc-100 rounded-xl p-1" data-testid="preview-tabs">
                  <button
                    onClick={() => setPreviewTab('details')}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      previewTab === 'details' ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-500 hover:text-zinc-700'
                    }`}
                    data-testid="tab-details"
                  >
                    <FileText size={14} /> Lead Details
                  </button>
                  <button
                    onClick={() => { setPreviewTab('proposal'); openProposalView(previewTrip); }}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      previewTab === 'proposal' ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-500 hover:text-zinc-700'
                    }`}
                    data-testid="tab-proposal"
                  >
                    <Eye size={14} /> Your Proposal
                  </button>
                </div>
              )}

              {/* DETAILS TAB */}
              {previewTab === 'details' && (
                <>
                  {/* Key Details Grid */}
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
                    {[
                      { icon: Calendar, label: 'Travel Dates', value: previewTrip.travel_dates?.start ? formatDate(previewTrip.travel_dates.start) : '—', sub: previewTrip.travel_dates?.end ? `to ${formatDate(previewTrip.travel_dates.end)}` : '' },
                      { icon: Users, label: 'Group Size', value: `${previewTrip.group_size} traveler${previewTrip.group_size !== 1 ? 's' : ''}`, sub: '' },
                      { icon: DollarSign, label: 'Budget Range', value: `$${previewTrip.budget_range?.min?.toLocaleString()} – $${previewTrip.budget_range?.max?.toLocaleString()}`, sub: '' },
                      { icon: Clock, label: 'Submitted', value: formatDate(previewTrip.created_at), sub: '' },
                    ].map((d, i) => (
                      <div key={i} className="border border-zinc-150 rounded-2xl p-4 bg-white" data-testid={`preview-detail-${i}`}>
                        <div className="flex items-center gap-2 mb-2.5">
                          <d.icon size={14} className="text-zinc-500" strokeWidth={2} />
                          <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">{d.label}</span>
                        </div>
                        <p className="text-sm font-bold text-zinc-900">{d.value}</p>
                        {d.sub && <p className="text-xs text-zinc-500 mt-0.5">{d.sub}</p>}
                      </div>
                    ))}
                  </div>

                  {/* Destinations & Interests */}
                  <div className="grid lg:grid-cols-2 gap-3 mb-6">
                    {previewTrip.destination_interests?.length > 0 && (
                      <div className="border border-zinc-150 rounded-2xl p-5 bg-white" data-testid="preview-interests">
                        <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-3">Interests</p>
                        <div className="flex flex-wrap gap-2">
                          {previewTrip.destination_interests.map(d => (
                            <span key={d} className="px-3 py-1.5 bg-zinc-50 text-zinc-900 text-sm font-semibold rounded-xl border border-zinc-200">{d}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {(previewTrip.specific_destinations?.length > 0 || previewTrip.open_to_suggestions?.length > 0) && (
                      <div className="border border-zinc-150 rounded-2xl p-5 bg-white" data-testid="preview-destinations">
                        <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-3">Destinations</p>
                        <div className="flex flex-wrap gap-2">
                          {previewTrip.specific_destinations?.map(d => (
                            <span key={d} className="px-3 py-1.5 bg-zinc-900 text-white text-sm font-semibold rounded-xl">{d}</span>
                          ))}
                          {previewTrip.open_to_suggestions?.map(d => (
                            <span key={d} className="px-3 py-1.5 bg-zinc-50 text-zinc-600 text-sm font-medium rounded-xl border border-dashed border-zinc-300">{d} (flexible)</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Preferences */}
                  {previewTrip.preferences?.length > 0 && (
                    <div className="border border-zinc-150 rounded-2xl p-5 mb-6 bg-white" data-testid="preview-prefs">
                      <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-3">Preferences</p>
                      <div className="flex flex-wrap gap-2">
                        {previewTrip.preferences.map(p => (
                          <span key={p} className="px-3 py-1.5 bg-zinc-50 text-zinc-800 text-sm font-semibold rounded-xl border border-zinc-200">{p}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Special Requests */}
                  {previewTrip.special_requests && (
                    <div className="border border-zinc-150 rounded-2xl p-5 mb-6 bg-white" data-testid="preview-special">
                      <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-2">Special Requests</p>
                      <p className="text-sm text-zinc-700 leading-relaxed font-medium">"{previewTrip.special_requests}"</p>
                    </div>
                  )}

                  {/* Bottom CTA */}
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pt-5 border-t border-zinc-200">
                    <p className="text-sm text-zinc-500">{myItineraries.filter(it => it.trip_id === previewTrip.id).length} proposal{myItineraries.filter(it => it.trip_id === previewTrip.id).length !== 1 ? 's' : ''} submitted for this lead</p>
                    <div className="flex items-center gap-2">
                      {findConversationForTrip(previewTrip.id) && (
                        <Button onClick={() => openMessageForLead(previewTrip.id)} variant="outline" className="border-zinc-200 text-zinc-700 hover:bg-zinc-50 rounded-xl px-4 py-2.5 text-sm font-semibold" data-testid="message-btn-bottom">
                          <MessageCircle size={15} className="mr-2" /> Message
                        </Button>
                      )}
                      {myItineraries.some(it => it.trip_id === previewTrip.id) ? (
                        <Button onClick={() => openProposalView(previewTrip)} className="bg-zinc-900 text-white hover:bg-zinc-800 rounded-xl px-5 py-2.5 text-sm font-semibold" data-testid="view-proposal-btn-bottom">
                          <Eye size={15} className="mr-2" /> View Proposal
                        </Button>
                      ) : (
                        <Button onClick={() => { openItineraryBuilder(previewTrip); setPreviewTrip(null); }} className="bg-zinc-900 text-white hover:bg-zinc-800 rounded-xl px-5 py-2.5 text-sm font-semibold" data-testid="create-proposal-btn-bottom">
                          <Plus size={15} className="mr-2" /> Create Proposal
                        </Button>
                      )}
                    </div>
                  </div>
                </>
              )}

              {/* PROPOSAL TAB */}
              {previewTab === 'proposal' && previewProposal && (
                <div data-testid="proposal-view">
                  {/* Proposal Status Banner */}
                  <div className={`rounded-2xl p-5 mb-6 border ${
                    previewProposal.status === 'selected' ? 'bg-emerald-50 border-emerald-200' :
                    previewProposal.status === 'viewed' ? 'bg-purple-50 border-purple-200' :
                    previewProposal.status === 'rejected' ? 'bg-red-50 border-red-200' :
                    'bg-zinc-50 border-zinc-200'
                  }`} data-testid="proposal-status-banner">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                          previewProposal.status === 'selected' ? 'bg-emerald-100' :
                          previewProposal.status === 'viewed' ? 'bg-purple-100' :
                          previewProposal.status === 'rejected' ? 'bg-red-100' :
                          'bg-zinc-100'
                        }`}>
                          {previewProposal.status === 'selected' ? <Check size={18} className="text-emerald-600" /> :
                           previewProposal.status === 'viewed' ? <Eye size={18} className="text-purple-600" /> :
                           previewProposal.status === 'rejected' ? <X size={18} className="text-red-600" /> :
                           <Clock size={18} className="text-zinc-500" />}
                        </div>
                        <div>
                          <p className={`text-sm font-bold ${
                            previewProposal.status === 'selected' ? 'text-emerald-800' :
                            previewProposal.status === 'viewed' ? 'text-purple-800' :
                            previewProposal.status === 'rejected' ? 'text-red-800' :
                            'text-zinc-800'
                          }`}>
                            {previewProposal.status === 'selected' ? 'Traveler Accepted' :
                             previewProposal.status === 'viewed' ? 'Traveler Viewed' :
                             previewProposal.status === 'rejected' ? 'Traveler Declined' :
                             'Awaiting Traveler Response'}
                          </p>
                          <p className="text-xs text-zinc-500 mt-0.5">
                            Submitted {formatDate(previewProposal.created_at)}
                            {previewProposal.status === 'selected' && ' - Booking confirmed'}
                          </p>
                        </div>
                      </div>
                      <Badge className={statusColors[previewProposal.status] || 'bg-zinc-100 text-zinc-600'}>
                        {previewProposal.status}
                      </Badge>
                    </div>
                  </div>

                  {/* Proposal Preview Card */}
                  <div className="bg-white rounded-2xl border border-zinc-200 overflow-hidden mb-6">
                    <div className="bg-zinc-900 p-6">
                      <h2 className="text-lg font-bold text-white">{previewProposal.title}</h2>
                      <p className="text-sm text-zinc-300 mt-1">{previewProposal.duration_days} days &middot; ${parseFloat(previewProposal.total_price || 0).toLocaleString()} {previewProposal.currency || 'USD'}</p>
                      {previewProposal.price_per_person && <p className="text-xs text-zinc-400 mt-0.5">${parseFloat(previewProposal.price_per_person).toLocaleString()} per person</p>}
                    </div>

                    <div className="p-6 space-y-6">
                      {/* Description */}
                      {previewProposal.description && (
                        <div>
                          <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mb-1.5">Overview</p>
                          <p className="text-sm text-zinc-700 leading-relaxed">{previewProposal.description}</p>
                        </div>
                      )}

                      {/* Highlights */}
                      {previewProposal.highlights?.filter(h => h?.trim()).length > 0 && (
                        <div>
                          <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mb-2">Highlights</p>
                          <div className="space-y-1.5">
                            {previewProposal.highlights.filter(h => h?.trim()).map((h, i) => (
                              <div key={i} className="flex items-start gap-2">
                                <Star size={12} className="text-amber-400 mt-0.5 shrink-0" />
                                <span className="text-sm text-zinc-700">{h}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Day-by-Day Plan */}
                      {previewProposal.daily_plan?.length > 0 && (
                        <div>
                          <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mb-3">Day-by-Day Itinerary</p>
                          <div className="space-y-3">
                            {previewProposal.daily_plan.map((day, idx) => (
                              <div key={idx} className="flex gap-4" data-testid={`proposal-day-${idx}`}>
                                <div className="flex flex-col items-center">
                                  <div className="w-7 h-7 rounded-lg bg-zinc-900 text-white text-xs font-bold flex items-center justify-center shrink-0">{day.day || idx + 1}</div>
                                  {idx < previewProposal.daily_plan.length - 1 && <div className="w-px flex-1 bg-zinc-200 mt-1" />}
                                </div>
                                <div className="flex-1 pb-4">
                                  <p className="text-sm font-semibold text-zinc-900">{day.title || `Day ${day.day || idx + 1}`}</p>
                                  {day.activities && <p className="text-xs text-zinc-600 mt-0.5">{Array.isArray(day.activities) ? day.activities.join(', ') : day.activities}</p>}
                                  <div className="flex flex-wrap gap-3 mt-1.5 text-[11px] text-zinc-400">
                                    {day.accommodation && <span>{day.accommodation}</span>}
                                    {day.transport && <span>&middot; {day.transport}</span>}
                                    {day.meals && <span>&middot; {Array.isArray(day.meals) ? day.meals.join(', ') : day.meals}</span>}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Logistics */}
                      {(previewProposal.accommodation_type || previewProposal.transport_type) && (
                        <div className="grid sm:grid-cols-2 gap-4">
                          {previewProposal.accommodation_type && (
                            <div>
                              <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mb-1.5">Accommodation</p>
                              <p className="text-sm text-zinc-700 capitalize">{previewProposal.accommodation_type}</p>
                              {previewProposal.lodge_names?.filter(n => n?.trim()).length > 0 && (
                                <p className="text-xs text-zinc-500 mt-0.5">{previewProposal.lodge_names.filter(n => n?.trim()).join(', ')}</p>
                              )}
                            </div>
                          )}
                          {previewProposal.transport_type && (
                            <div>
                              <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mb-1.5">Transport</p>
                              <p className="text-sm text-zinc-700">{previewProposal.transport_type}</p>
                              <div className="flex gap-3 mt-1 text-xs text-zinc-500">
                                {previewProposal.guide_included && <span>Guide included</span>}
                                {previewProposal.transfers_included && <span>Transfers included</span>}
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Inclusions / Exclusions */}
                      <div className="grid sm:grid-cols-2 gap-4">
                        {previewProposal.inclusions?.length > 0 && (
                          <div>
                            <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mb-2">Included</p>
                            <div className="space-y-1">
                              {previewProposal.inclusions.map(item => (
                                <div key={item} className="flex items-center gap-2 text-sm text-zinc-700">
                                  <Check size={12} className="text-emerald-500 shrink-0" /> {item}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {previewProposal.exclusions?.length > 0 && (
                          <div>
                            <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mb-2">Excluded</p>
                            <div className="space-y-1">
                              {previewProposal.exclusions.map(item => (
                                <div key={item} className="flex items-center gap-2 text-sm text-zinc-500">
                                  <X size={12} className="text-red-400 shrink-0" /> {item}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Bottom Actions */}
                  <div className="flex items-center justify-between pt-5 border-t border-zinc-200">
                    <button onClick={() => setPreviewTab('details')} className="text-sm text-zinc-500 hover:text-zinc-900 font-medium flex items-center gap-1 transition">
                      <ChevronLeft size={14} /> Back to Lead Details
                    </button>
                    <div className="flex items-center gap-2">
                      {findConversationForTrip(previewTrip.id) && (
                        <Button onClick={() => openMessageForLead(previewTrip.id)} variant="outline" className="border-zinc-200 text-zinc-700 hover:bg-zinc-50 rounded-xl px-4 py-2.5 text-sm font-semibold" data-testid="proposal-message-btn">
                          <MessageCircle size={15} className="mr-2" /> Message Traveler
                        </Button>
                      )}
                      <Button onClick={() => { openItineraryBuilder(previewTrip); setPreviewTrip(null); }} variant="outline" className="border-zinc-200 text-zinc-700 hover:bg-zinc-50 rounded-xl px-4 py-2.5 text-sm font-semibold" data-testid="send-new-version-btn">
                        <Plus size={15} className="mr-2" /> New Version
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ===== CURATED ITINERARIES ===== */}
          {activeView === 'itineraries' && (
            <div data-testid="itineraries-view">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-8">
                <div>
                  <h1 className="text-lg font-semibold text-zinc-900">Curated Itineraries</h1>
                  <p className="text-sm text-zinc-500 mt-0.5">{myItineraries.length} itinerar{myItineraries.length !== 1 ? 'ies' : 'y'} created &middot; {myItineraries.filter(it => it.is_published && it.hosting_active).length} published</p>
                </div>
                <Button onClick={() => navigate('/operator/create-curated')} className="bg-zinc-900 text-white hover:bg-zinc-800 rounded-xl px-5 py-2.5 text-sm font-semibold shrink-0" data-testid="create-curated-btn">
                  <Plus size={15} className="mr-2" /> Create Itinerary
                </Button>
              </div>

              {myItineraries.length === 0 ? (
                <div className="text-center py-20 bg-white rounded-2xl border border-zinc-100">
                  <Globe size={48} className="mx-auto mb-4 text-zinc-200" strokeWidth={1.2} />
                  <h3 className="font-semibold text-zinc-900 mb-1">No itineraries yet</h3>
                  <p className="text-sm text-zinc-400 max-w-sm mx-auto mb-5">
                    Create curated itineraries to showcase on the Explore page and attract travelers.
                  </p>
                  <Button onClick={() => navigate('/operator/create-curated')} className="bg-zinc-900 text-white hover:bg-zinc-800 rounded-xl px-5 py-2.5 text-sm font-medium">
                    <Plus size={15} className="mr-2" /> Create Your First Itinerary
                  </Button>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 gap-4">
                  {myItineraries.map(it => {
                    const isPublished = it.is_published && it.hosting_active;
                    const isFeatured = it.is_promoted;
                    const featureRequested = it.feature_requested && !it.is_promoted;

                    return (
                      <div
                        key={it.id}
                        className="bg-white rounded-2xl border border-zinc-100 overflow-hidden hover:border-zinc-300 transition group"
                        data-testid={`my-itinerary-${it.id}`}
                      >
                        {/* Cover Image / Header */}
                        <div className="bg-zinc-900 p-5 relative">
                          {it.cover_image && (
                            <img src={`${API_URL}/api/files/${it.cover_image}?auth=${localStorage.getItem('karibu_token')}`} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
                          )}
                          <div className="relative z-10">
                            <div className="flex items-center gap-2 mb-2">
                              {isPublished && (
                                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-300">Published</span>
                              )}
                              {isFeatured && (
                                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 flex items-center gap-1"><Sparkles size={9} /> Featured</span>
                              )}
                              {featureRequested && (
                                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-300">Feature Requested</span>
                              )}
                              {it.status === 'selected' && (
                                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 flex items-center gap-1"><Check size={9} /> Booked</span>
                              )}
                            </div>
                            <h3 className="text-white font-bold text-base">{it.title}</h3>
                            <p className="text-zinc-300 text-sm mt-0.5">{it.duration_days} days &middot; ${parseFloat(it.total_price || 0).toLocaleString()} {it.currency || 'USD'}</p>
                          </div>
                        </div>

                        {/* Content */}
                        <div className="p-5">
                          {it.description && (
                            <p className="text-sm text-zinc-500 line-clamp-2 mb-3">{it.description}</p>
                          )}
                          {it.inclusions?.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mb-4">
                              {it.inclusions.slice(0, 4).map(inc => (
                                <span key={inc} className="text-[10px] bg-zinc-50 text-zinc-600 px-2 py-0.5 rounded-lg border border-zinc-100">{inc}</span>
                              ))}
                              {it.inclusions.length > 4 && <span className="text-[10px] text-zinc-400">+{it.inclusions.length - 4}</span>}
                            </div>
                          )}

                          {/* Actions */}
                          <div className="flex items-center gap-2 pt-3 border-t border-zinc-100">
                            {!isPublished && (
                              <Button onClick={() => publishItinerary(it.id)} size="sm" className="bg-zinc-900 text-white hover:bg-zinc-800 rounded-lg px-3 py-1.5 text-xs h-auto font-semibold" data-testid={`publish-btn-${it.id}`}>
                                <Globe size={12} className="mr-1" /> Publish
                              </Button>
                            )}
                            {isPublished && (
                              <Button onClick={() => unpublishItinerary(it.id)} size="sm" variant="outline" className="border-zinc-200 text-zinc-600 hover:bg-zinc-50 rounded-lg px-3 py-1.5 text-xs h-auto font-medium" data-testid={`unpublish-btn-${it.id}`}>
                                <EyeOff size={12} className="mr-1" /> Unpublish
                              </Button>
                            )}
                            {isPublished && !isFeatured && !featureRequested && (
                              <Button onClick={() => requestFeaturing(it.id)} size="sm" variant="outline" className="border-amber-200 text-amber-700 hover:bg-amber-50 rounded-lg px-3 py-1.5 text-xs h-auto font-medium" data-testid={`feature-btn-${it.id}`}>
                                <Sparkles size={12} className="mr-1" /> Request Featuring
                              </Button>
                            )}
                            <div className="ml-auto flex items-center gap-1">
                              {it.status !== 'selected' && (
                                <button onClick={() => deleteItinerary(it.id)} className="p-1.5 hover:bg-red-50 rounded-lg text-zinc-400 hover:text-red-500 transition" data-testid={`delete-itin-${it.id}`}>
                                  <Trash2 size={14} />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ===== BOOKINGS ===== */}
          {activeView === 'bookings' && (
            <div data-testid="bookings-view">
              <div className="mb-6">
                <h1 className="text-lg font-semibold text-zinc-900">Confirmed Bookings</h1>
                <p className="text-neutral-500 mt-1 text-sm">Manage your confirmed bookings and generate invoices</p>
              </div>
              {confirmedBookings.length === 0 ? (
                <div className="text-center py-16 bg-white rounded-xl border border-zinc-100">
                  <BookOpen size={40} className="mx-auto text-zinc-200 mb-3" />
                  <p className="text-zinc-400 text-sm">No confirmed bookings yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {confirmedBookings.map(bk => (
                    <div key={bk.id} className="bg-white rounded-xl border border-zinc-100 p-5" data-testid={`booking-${bk.id}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4">
                          <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
                            <BookOpen size={18} className="text-emerald-600" />
                          </div>
                          <div>
                            <p className="text-sm font-bold text-zinc-900">{bk.itinerary_title || 'Trip Booking'}</p>
                            <p className="text-xs text-zinc-400 mt-0.5">{bk.booking_ref}</p>
                            <div className="flex items-center gap-3 mt-2 text-xs text-zinc-500">
                              <span className="flex items-center gap-1"><User size={12} /> {bk.traveler_name}</span>
                              {bk.traveler_phone && <span className="flex items-center gap-1"><Phone size={12} /> {bk.traveler_phone}</span>}
                              {bk.traveler_email && <span className="flex items-center gap-1"><Mail size={12} /> {bk.traveler_email}</span>}
                            </div>
                            <div className="flex items-center gap-3 mt-1.5 text-xs text-zinc-400">
                              <span className="flex items-center gap-1"><Calendar size={12} /> {bk.created_at ? format(new Date(bk.created_at), 'MMM dd, yyyy') : ''}</span>
                              <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px]">Confirmed</Badge>
                            </div>
                          </div>
                        </div>
                        <div className="text-right flex flex-col gap-2">
                          {bk.invoice ? (
                            <div className="flex flex-col items-end gap-1.5">
                              <div className="text-right">
                                <p className="text-xs text-zinc-400">Invoice</p>
                                <p className="text-sm font-bold text-zinc-900">{bk.invoice.invoice_number}</p>
                                <p className="text-xs text-zinc-500">${bk.invoice.total_cost?.toLocaleString()}</p>
                              </div>
                              <button onClick={() => viewBookingInvoice(bk.id)}
                                className="text-xs text-[#073763] hover:text-[#052a4a] font-medium flex items-center gap-1"
                                data-testid={`view-invoice-${bk.id}`}>
                                <Eye size={12} /> View / Print
                              </button>
                            </div>
                          ) : (
                            <Button
                              onClick={() => { setInvoiceDialog(bk); setInvoiceForm({ cost_per_person_per_day: '', number_of_people: bk.group_size || '', number_of_days: '', notes: '' }); }}
                              className="bg-[#073763] text-white hover:bg-[#052a4a] rounded-lg px-3 py-2 text-xs font-medium"
                              data-testid={`gen-invoice-${bk.id}`}
                            >
                              <Receipt size={12} className="mr-1" /> Generate Invoice
                            </Button>
                          )}
                          <button onClick={() => previewTraveler(bk.traveler_id)}
                            className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1 justify-end"
                            data-testid={`view-traveler-bk-${bk.id}`}
                          >
                            <Eye size={12} /> View Traveler
                          </button>
                          <button onClick={() => { setReviewDialog(bk); setReviewRating(5); setReviewComment(''); }}
                            className="text-xs text-amber-600 hover:text-amber-800 font-medium flex items-center gap-1 justify-end"
                            data-testid={`review-bk-${bk.id}`}
                          >
                            <Star size={12} /> Review Traveler
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ===== WALLET ===== */}
          {activeView === 'wallet' && (
            <div data-testid="wallet-view">
              <div className="mb-8">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                  <div>
                    <h1 className="text-lg font-semibold text-zinc-900">Credit Wallet</h1>
                    <p className="text-neutral-500 mt-1 text-sm">Manage your credits for leads, proposals, publishing & promotions</p>
                  </div>
                  <Button onClick={() => { setGeneratedInvoice(null); setShowTopupDialog(true); }} className="bg-[#073763] text-white hover:bg-[#073763]/90 rounded-lg px-5 py-2.5 text-sm font-medium shrink-0" data-testid="topup-btn">
                    <Plus size={15} className="mr-1.5" /> Top Up Credits
                  </Button>
                </div>
              </div>

              {/* Balance + Analytics Banner */}
              <div className="bg-zinc-900 rounded-2xl p-6 text-white mb-6" data-testid="wallet-balance">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-zinc-400 text-xs uppercase tracking-wider font-medium">Available Balance</p>
                    <p className="text-4xl font-bold mt-1">{Math.floor(wallet?.balance || 0).toLocaleString()}</p>
                    <p className="text-zinc-500 text-xs mt-1">1 {wallet?.currency || 'USD'} = {wallet?.credit_rate || 10} credits</p>
                  </div>
                  <div className="text-right">
                    <p className="text-zinc-400 text-xs uppercase tracking-wider font-medium">Total Spent</p>
                    <p className="text-2xl font-bold mt-1 text-white">
                      {Math.floor(wallet?.transactions?.filter(t => t.amount < 0).reduce((sum, t) => sum + Math.abs(t.amount), 0) || 0).toLocaleString()}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-5 pt-5 border-t border-white/10">
                  <div>
                    <p className="text-zinc-400 text-[10px] uppercase tracking-wider font-medium">Leads Unlocked</p>
                    <p className="text-xl font-bold mt-0.5">{wallet?.unlocked_leads || 0}</p>
                  </div>
                  <div>
                    <p className="text-zinc-400 text-[10px] uppercase tracking-wider font-medium">Proposals Sent</p>
                    <p className="text-xl font-bold mt-0.5">{myItineraries.length}</p>
                  </div>
                  <div>
                    <p className="text-zinc-400 text-[10px] uppercase tracking-wider font-medium">Active Promos</p>
                    <p className="text-xl font-bold mt-0.5">{wallet?.active_promotions || 0}</p>
                  </div>
                  <div>
                    <p className="text-zinc-400 text-[10px] uppercase tracking-wider font-medium">Published</p>
                    <p className="text-xl font-bold mt-0.5">{wallet?.published_itineraries || 0}</p>
                  </div>
                </div>
              </div>

              {/* Top-Up Request History */}
              {topupRequests.length > 0 && (
                <div className="mb-8">
                  <h3 className="font-semibold text-zinc-900 mb-4">Top-Up Requests</h3>
                  <div className="space-y-3">
                    {topupRequests.map(req => (
                      <div key={req.id} className="bg-white rounded-xl border border-zinc-100 p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3" data-testid={`topup-req-${req.invoice_number}`}>
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-lg bg-zinc-100 flex items-center justify-center">
                            <FileText size={18} className="text-zinc-500" />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-zinc-900">{req.invoice_number}</p>
                            <p className="text-xs text-zinc-400">{format(new Date(req.created_at), 'MMM dd, yyyy HH:mm')}</p>
                          </div>
                        </div>
                        <div className="text-right flex items-center gap-4">
                          <div>
                            <p className="text-sm font-bold text-zinc-900">{req.credits_requested.toLocaleString()} credits</p>
                            <p className="text-xs text-zinc-400">{req.currency} {req.amount.toLocaleString()}</p>
                          </div>
                          <Badge className={
                            req.status === 'pending' ? 'bg-amber-50 text-amber-700 border border-amber-200' :
                            req.status === 'paid' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :
                            'bg-red-50 text-red-700 border border-red-200'
                          }>
                            {req.status === 'pending' ? 'Pending' : req.status === 'paid' ? 'Paid' : 'Rejected'}
                          </Badge>
                          <button onClick={() => setCreditInvoicePreview(req)}
                            className="text-xs text-[#073763] hover:text-[#052a4a] font-medium flex items-center gap-1"
                            data-testid={`view-credit-invoice-${req.invoice_number}`}>
                            <Eye size={12} /> View
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Activity Costs (Collapsible) */}
              <div className="mb-8">
                <button
                  onClick={() => setShowActivityCosts(prev => !prev)}
                  className="flex items-center gap-2 text-sm font-semibold text-zinc-700 hover:text-zinc-900 transition"
                  data-testid="toggle-activity-costs"
                >
                  <CreditCard size={15} />
                  View Activity Costs
                  <ChevronRight size={14} className={`transition-transform ${showActivityCosts ? 'rotate-90' : ''}`} />
                </button>
                {showActivityCosts && (
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-3">
                    {wallet?.activities && Object.entries(wallet.activities)
                      .filter(([key]) => key !== 'lead_received')
                      .map(([key, act]) => (
                      <div key={key} className={`bg-white rounded-xl border p-4 ${act.enabled ? 'border-zinc-100' : 'border-dashed border-zinc-200 opacity-60'}`} data-testid={`activity-${key}`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-medium text-zinc-500">{act.label}</span>
                          {act.enabled ? (
                            <span className="text-[10px] bg-emerald-50 text-emerald-600 px-2 py-0.5 rounded-full font-medium">Active</span>
                          ) : (
                            <span className="text-[10px] bg-zinc-100 text-zinc-400 px-2 py-0.5 rounded-full font-medium">Disabled</span>
                          )}
                        </div>
                        <p className="text-xl font-bold text-zinc-900">{act.cost} <span className="text-xs font-normal text-zinc-400">credits</span></p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Transaction History with Pagination */}
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-zinc-900">Transaction History</h3>
                {wallet?.transactions?.length > 0 && (
                  <span className="text-xs text-zinc-400">{wallet.transactions.length} transactions</span>
                )}
              </div>
              {(!wallet?.transactions || wallet.transactions.length === 0) ? (
                <div className="text-center py-12 bg-white rounded-xl border border-zinc-100">
                  <Wallet size={36} className="mx-auto text-zinc-200 mb-3" />
                  <p className="text-zinc-400 text-sm">No transactions yet. Buy credits to get started.</p>
                </div>
              ) : (
                <>
                  <div className="space-y-2" data-testid="transactions-cards">
                    {wallet.transactions.slice((txPage - 1) * txPerPage, txPage * txPerPage).map(tx => {
                      const isCredit = tx.amount > 0;
                      const typeConfig = {
                        topup: { label: 'Top-Up', icon: ArrowDown, colors: 'bg-emerald-50 text-emerald-600 border-emerald-100' },
                        admin_credit: { label: 'Credit', icon: CreditCard, colors: 'bg-emerald-50 text-emerald-600 border-emerald-100' },
                        lead_unlock: { label: 'Lead Unlock', icon: Unlock, colors: 'bg-blue-50 text-blue-600 border-blue-100' },
                        lead_fee: { label: 'Lead Unlock', icon: Unlock, colors: 'bg-blue-50 text-blue-600 border-blue-100' },
                        proposal_submission: { label: 'Proposal', icon: Send, colors: 'bg-purple-50 text-purple-600 border-purple-100' },
                        itinerary_publish: { label: 'Publish', icon: Eye, colors: 'bg-cyan-50 text-cyan-600 border-cyan-100' },
                        itinerary_hosting: { label: 'Hosting', icon: Eye, colors: 'bg-cyan-50 text-cyan-600 border-cyan-100' },
                        promotion: { label: 'Promo', icon: Zap, colors: 'bg-amber-50 text-amber-600 border-amber-100' },
                      };
                      const cfg = typeConfig[tx.type] || { label: tx.type, icon: DollarSign, colors: 'bg-zinc-50 text-zinc-600 border-zinc-100' };
                      const IconComp = cfg.icon;
                      return (
                        <div key={tx.id} className="bg-white rounded-xl border border-zinc-100 p-4 flex items-center gap-4 hover:shadow-sm transition" data-testid={`tx-card-${tx.id}`}>
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${isCredit ? 'bg-emerald-50' : 'bg-red-50'}`}>
                            <IconComp size={18} className={isCredit ? 'text-emerald-600' : 'text-red-500'} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <Badge className={`text-[10px] ${cfg.colors}`}>{cfg.label}</Badge>
                              <span className="text-xs text-zinc-400">{formatDate(tx.created_at)}</span>
                            </div>
                            <p className="text-sm text-zinc-600 mt-0.5 truncate">{tx.description}</p>
                          </div>
                          <div className="text-right shrink-0">
                            <p className={`text-sm font-bold ${isCredit ? 'text-emerald-600' : 'text-red-500'}`}>
                              {isCredit ? '+' : ''}{Math.floor(tx.amount)} <span className="text-[10px] font-normal text-zinc-400">credits</span>
                            </p>
                            <p className="text-xs text-zinc-400 mt-0.5">Bal: {Math.floor(tx.balance_after || 0)}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {/* Transaction Pagination */}
                  <div className="flex items-center justify-between pt-4">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-zinc-500">Per page:</span>
                      <select
                        value={txPerPage}
                        onChange={e => { setTxPerPage(Number(e.target.value)); setTxPage(1); }}
                        className="text-xs border border-zinc-200 rounded-md px-2 py-1 text-zinc-700 bg-white focus:outline-none"
                        data-testid="tx-per-page"
                      >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                      </select>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-zinc-500">
                        {Math.min((txPage - 1) * txPerPage + 1, wallet.transactions.length)}–{Math.min(txPage * txPerPage, wallet.transactions.length)} of {wallet.transactions.length}
                      </span>
                      <div className="flex gap-1">
                        <button onClick={() => setTxPage(p => Math.max(1, p - 1))} disabled={txPage === 1} className="p-1.5 rounded-md border border-zinc-200 text-zinc-500 hover:bg-zinc-50 disabled:opacity-30 transition" data-testid="tx-prev">
                          <ChevronLeft size={14} />
                        </button>
                        <button onClick={() => setTxPage(p => Math.min(Math.ceil(wallet.transactions.length / txPerPage), p + 1))} disabled={txPage >= Math.ceil(wallet.transactions.length / txPerPage)} className="p-1.5 rounded-md border border-zinc-200 text-zinc-500 hover:bg-zinc-50 disabled:opacity-30 transition" data-testid="tx-next">
                          <ChevronRight size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* ===== TEAM ACCOUNTS ===== */}
          {activeView === 'team_accounts' && (
            <div data-testid="team-accounts-view">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-bold text-zinc-900">Team Accounts</h2>
                  <p className="text-sm text-zinc-400 mt-1">Create accounts for your team to manage leads and content</p>
                </div>
                <button onClick={() => { setTeamAccountForm({ name: '', phone: '', pin: '1234', team_roles: ['leads_agent'] }); setShowTeamAccountDialog(true); }}
                  className="bg-zinc-900 text-white hover:bg-zinc-800 rounded-lg px-4 py-2.5 text-sm font-medium flex items-center gap-1.5 transition" data-testid="create-team-account-btn">
                  <Plus size={15} /> Create Account
                </button>
              </div>

              {/* Role legend */}
              <div className="bg-zinc-50 rounded-xl p-4 mb-6 flex flex-wrap gap-6">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center mt-0.5"><Inbox size={15} className="text-blue-600" /></div>
                  <div>
                    <p className="text-sm font-semibold text-zinc-900">Leads Agent</p>
                    <p className="text-xs text-zinc-400">Manage leads, create proposals, customer messaging</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center mt-0.5"><Globe size={15} className="text-purple-600" /></div>
                  <div>
                    <p className="text-sm font-semibold text-zinc-900">Content / Marketing</p>
                    <p className="text-xs text-zinc-400">Manage curated itineraries, publishing, featuring</p>
                  </div>
                </div>
              </div>

              {teamAccounts.length === 0 ? (
                <div className="text-center py-20 bg-white rounded-xl border border-zinc-100">
                  <Users size={36} className="mx-auto text-zinc-200 mb-3" />
                  <p className="text-zinc-500 mb-1">No team accounts yet</p>
                  <p className="text-xs text-zinc-400 mb-4">Create accounts so your team can help manage operations</p>
                  <button onClick={() => setShowTeamAccountDialog(true)}
                    className="bg-zinc-900 text-white rounded-lg px-4 py-2 text-sm font-medium hover:bg-zinc-800 transition" data-testid="create-first-account-btn">
                    Create First Account
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {teamAccounts.map(acc => (
                    <div key={acc.id} className="bg-white rounded-xl border border-zinc-100 p-5 flex items-center gap-4" data-testid={`team-acc-${acc.id}`}>
                      <div className="w-11 h-11 rounded-full bg-purple-100 flex items-center justify-center text-purple-600 font-bold flex-shrink-0">
                        {(acc.name || 'T').charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-zinc-900 text-sm">{acc.name}</p>
                        <p className="text-xs text-zinc-400 font-mono">+{acc.phone}</p>
                      </div>
                      <div className="flex gap-1.5 flex-shrink-0">
                        {acc.team_roles?.includes('leads_agent') && (
                          <span className="text-[10px] bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full font-medium">Leads Agent</span>
                        )}
                        {acc.team_roles?.includes('content_marketing') && (
                          <span className="text-[10px] bg-purple-50 text-purple-700 px-2 py-0.5 rounded-full font-medium">Content</span>
                        )}
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <span className="text-[10px] text-zinc-400">{acc.created_at ? new Date(acc.created_at).toLocaleDateString() : ''}</span>
                        <button onClick={() => deleteTeamAccount(acc.id)}
                          className="p-2 hover:bg-red-50 rounded-lg text-zinc-400 hover:text-red-500 transition" data-testid={`delete-team-acc-${acc.id}`}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}


          {/* ===== MESSAGES (WhatsApp-style split view) ===== */}
          {(activeView === 'messages' || activeView === 'chat') && (
            <div data-testid="op-messages-view" className="flex rounded-2xl border border-zinc-200 bg-white overflow-hidden" style={{ height: 'calc(100vh - 80px)', maxWidth: '100%' }}>
              {/* Left: Conversation List — hidden on mobile when chat is open */}
              <div className={`${activeConversation ? 'hidden md:flex' : 'flex'} w-full md:w-[280px] border-r border-zinc-200 flex-col shrink-0`}>
                <div className="p-4 border-b border-zinc-100">
                  <h2 className="text-base font-semibold text-zinc-900">Messages</h2>
                  <p className="text-xs text-zinc-400 mt-0.5">{conversations.length} conversation{conversations.length !== 1 ? 's' : ''}</p>
                </div>
                <div className="flex-1 overflow-y-auto">
                  {conversations.length === 0 ? (
                    <div className="text-center py-16 px-4">
                      <MessageCircle className="mx-auto mb-3 text-zinc-200" size={32} />
                      <p className="text-sm text-zinc-400">No conversations yet</p>
                    </div>
                  ) : (
                    conversations.map(convo => (
                      <button
                        key={convo.id}
                        onClick={() => openOpConversation(convo)}
                        className={`w-full p-4 text-left flex items-center gap-3 transition-colors border-b border-zinc-50 ${
                          activeConversation?.id === convo.id ? 'bg-zinc-50' : 'hover:bg-zinc-50/50'
                        }`}
                        data-testid={`op-convo-${convo.id}`}
                      >
                        <div className="relative shrink-0">
                          <div className="w-11 h-11 rounded-2xl bg-zinc-900 flex items-center justify-center text-white text-sm font-bold">
                            {convo.traveler_name?.charAt(0)?.toUpperCase() || 'T'}
                          </div>
                          {convo.unread_operator > 0 && (
                            <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 bg-emerald-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                              {convo.unread_operator}
                            </span>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className="font-semibold text-zinc-900 text-sm truncate">{convo.traveler_name}</p>
                            <span className="text-[10px] text-zinc-400 shrink-0 ml-2">{formatDate(convo.last_message_at)}</span>
                          </div>
                          <p className="text-[11px] text-zinc-400 mt-0.5 truncate">{convo.trip_summary}</p>
                          <p className="text-xs text-zinc-500 mt-0.5 truncate">{convo.last_message}</p>
                        </div>
                      </button>
                    ))
                  )}
                </div>
              </div>

              {/* Right: Chat Area — full width on mobile */}
              <div className={`${!activeConversation ? 'hidden md:flex' : 'flex'} flex-1 flex-col min-w-0`}>
                {!activeConversation ? (
                  <div className="flex-1 flex items-center justify-center bg-zinc-50/50">
                    <div className="text-center">
                      <div className="w-16 h-16 rounded-2xl bg-zinc-100 flex items-center justify-center mx-auto mb-3">
                        <MessageCircle size={28} className="text-zinc-300" />
                      </div>
                      <p className="text-sm font-medium text-zinc-500">Select a conversation</p>
                      <p className="text-xs text-zinc-400 mt-1">Choose from your conversations on the left</p>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Chat Header */}
                    <div className="flex items-center gap-3 px-4 sm:px-5 py-3 border-b border-zinc-100 bg-white shrink-0">
                      <button onClick={() => setActiveConversation(null)} className="md:hidden p-1 -ml-1 text-zinc-500 hover:text-zinc-900" data-testid="op-chat-back-btn">
                        <ChevronLeft size={20} />
                      </button>
                      <div className="relative">
                        <div className="w-10 h-10 rounded-2xl bg-zinc-900 flex items-center justify-center text-white text-sm font-bold">
                          {activeConversation.traveler_name?.charAt(0)?.toUpperCase() || 'T'}
                        </div>
                        <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-emerald-500 border-2 border-white rounded-full" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-zinc-900 text-sm">{activeConversation.traveler_name}</p>
                        <p className="text-[11px] text-zinc-400 truncate">{activeConversation.trip_summary}</p>
                      </div>
                    </div>

                    {/* Messages Area */}
                    <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3 bg-zinc-50/30" data-testid="op-chat-messages">
                      {opMessages.map((msg, idx) => {
                        const isMe = msg.sender_id === user?.id;
                        const isSystem = msg.sender_role === 'system';
                        const showTime = idx === 0 || (new Date(msg.created_at) - new Date(opMessages[idx - 1]?.created_at)) > 300000;
                        return (
                          <div key={msg.id}>
                            {showTime && (
                              <div className="text-center my-3">
                                <span className="text-[10px] text-zinc-400 bg-white px-3 py-1 rounded-full border border-zinc-100">{formatTime(msg.created_at)}</span>
                              </div>
                            )}
                            <div className={`flex ${isSystem ? 'justify-center' : isMe ? 'justify-end' : 'justify-start'}`}>
                              {isSystem ? (
                                <div className="bg-white text-zinc-500 text-xs px-4 py-2 rounded-full max-w-md text-center border border-zinc-100">{msg.content}</div>
                              ) : (
                                <div className={`max-w-[70%]`}>
                                  <div className={`px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                                    isMe ? 'bg-zinc-900 text-white rounded-br-md' : 'bg-white text-zinc-900 rounded-bl-md border border-zinc-100'
                                  }`}><p>{msg.content}</p></div>
                                  <div className={`flex items-center gap-1.5 mt-1 px-1 ${isMe ? 'justify-end' : 'justify-start'}`}>
                                    <span className="text-[10px] text-zinc-400">{formatTime(msg.created_at)}</span>
                                    {isMe && <span className="text-[10px] text-zinc-400 flex items-center"><Check size={10} className="text-emerald-500" /><Check size={10} className="-ml-1.5 text-emerald-500" /></span>}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                      {isTyping && (
                        <div className="flex justify-start" data-testid="typing-indicator">
                          <div className="bg-white rounded-2xl rounded-bl-md px-4 py-3 flex items-center gap-1.5 border border-zinc-100">
                            <span className="w-2 h-2 bg-zinc-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                            <span className="w-2 h-2 bg-zinc-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                            <span className="w-2 h-2 bg-zinc-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                          </div>
                        </div>
                      )}
                      <div ref={opMsgEndRef} />
                    </div>

                    {/* Message Input */}
                    <div className="px-5 py-3 border-t border-zinc-100 bg-white shrink-0">
                      <div className="flex items-end gap-2">
                        <div className="flex-1 bg-zinc-50 border border-zinc-200 rounded-2xl px-4 py-2.5 focus-within:border-zinc-400 focus-within:bg-white transition">
                          <Input
                            value={opNewMessage}
                            onChange={e => setOpNewMessage(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendOpMessage(); } }}
                            placeholder="Type a message..."
                            className="border-0 p-0 h-auto bg-transparent shadow-none focus-visible:ring-0 text-sm"
                            data-testid="op-chat-input"
                          />
                        </div>
                        <Button
                          onClick={sendOpMessage}
                          disabled={sendingMsg || !opNewMessage.trim()}
                          className="bg-zinc-900 text-white hover:bg-zinc-800 rounded-2xl w-10 h-10 p-0 flex items-center justify-center shrink-0 disabled:opacity-30"
                          data-testid="op-chat-send"
                        >
                          {sendingMsg ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}


          {/* ===== COMPANY PROFILE ===== */}
          {activeView === 'profile' && (
            <div data-testid="profile-view">
              <div className="mb-8">
 
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                  <div>
                    <h1 className="text-lg font-semibold text-zinc-900">Company Profile</h1>
                    <p className="text-zinc-500 mt-1 text-sm">Manage your operator profile visible to travelers</p>
                  </div>
                  {!editingProfile && (
                    <Button onClick={startEditProfile} className="bg-white border border-zinc-200 text-zinc-700 hover:bg-zinc-50 rounded-xl px-4 py-2 text-sm font-medium shrink-0" data-testid="edit-profile-btn">
                      <Pencil size={14} className="mr-2" /> Edit Profile
                    </Button>
                  )}
                </div>
              </div>

              {!editingProfile ? (
                /* View Mode */
                <div className="space-y-6">
                  {/* Hero Card */}
                  <div className="bg-zinc-900 rounded-2xl p-5 sm:p-6" data-testid="profile-hero">
                    <div className="flex items-center gap-4 sm:gap-6">
                      <div className="relative shrink-0">
                        {getAvatarUrl() ? (
                          <img src={getAvatarUrl()} alt="Company" className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl object-cover" />
                        ) : (
                          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-white/15 flex items-center justify-center text-white text-2xl sm:text-3xl font-bold">
                            {(profile?.company_name || 'O').charAt(0).toUpperCase()}
                          </div>
                        )}
                        <label className={`absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-white border-2 border-zinc-900 flex items-center justify-center cursor-pointer hover:bg-zinc-100 transition ${uploadingAvatar ? 'opacity-50' : ''}`}>
                          <Camera size={12} className="text-zinc-600" />
                          <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={uploadAvatar} data-testid="company-avatar-upload" />
                        </label>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h2 className="text-lg sm:text-xl font-bold text-white truncate" data-testid="company-name">{profile?.company_name || 'Your Company'}</h2>
                        <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                          <Badge className="bg-emerald-500/20 text-emerald-300 border-0 text-[10px]">Verified Operator</Badge>
                          {profile?.license_number && <span className="text-xs text-zinc-400">License: {profile.license_number}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-6 sm:gap-8 mt-4 pt-4 border-t border-white/10">
                      <div className="text-center">
                        <p className="text-xl sm:text-2xl font-bold text-white">{myItineraries.length}</p>
                        <p className="text-[10px] text-zinc-400 uppercase tracking-wider">Itineraries</p>
                      </div>
                      <div className="text-center">
                        <p className="text-xl sm:text-2xl font-bold text-white">{stats?.selected_itineraries || 0}</p>
                        <p className="text-[10px] text-zinc-400 uppercase tracking-wider">Bookings</p>
                      </div>
                      <div className="text-center">
                        <p className="text-xl sm:text-2xl font-bold text-white">{profile?.years_experience || 0}</p>
                        <p className="text-[10px] text-zinc-400 uppercase tracking-wider">Years Exp</p>
                      </div>
                    </div>
                  </div>

                  {/* Info Grid - 3 columns */}
                  <div className="grid lg:grid-cols-3 gap-4">
                    {/* About + Contact */}
                    <div className="lg:col-span-2 space-y-4">
                      <div className="bg-white rounded-2xl border border-zinc-100 p-6">
                        <h3 className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mb-3">About</h3>
                        <p className="text-sm text-zinc-700 leading-relaxed">{profile?.description || 'No description yet.'}</p>
                      </div>

                      {/* Services + Locations Row */}
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div className="bg-white rounded-2xl border border-zinc-100 p-6">
                          <h3 className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mb-3">Services Offered</h3>
                          <div className="flex flex-wrap gap-2">
                            {(profile?.services || []).length > 0 ? profile.services.map(s => (
                              <span key={s} className="text-xs bg-zinc-900 text-white px-3 py-1.5 rounded-lg font-medium">{s}</span>
                            )) : <p className="text-sm text-zinc-400">No services listed yet.</p>}
                          </div>
                        </div>
                        <div className="bg-white rounded-2xl border border-zinc-100 p-6">
                          <h3 className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mb-3">Locations Covered</h3>
                          <div className="flex flex-wrap gap-2">
                            {(profile?.locations || []).length > 0 ? profile.locations.map(l => (
                              <span key={l} className="text-xs bg-zinc-50 text-zinc-700 px-3 py-1.5 rounded-lg font-medium border border-zinc-200 flex items-center gap-1">
                                <MapPin size={10} /> {l}
                              </span>
                            )) : <p className="text-sm text-zinc-400">No locations listed yet.</p>}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Right Column: Contact + Languages */}
                    <div className="space-y-4">
                      <div className="bg-white rounded-2xl border border-zinc-100 p-6">
                        <h3 className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mb-4">Contact Info</h3>
                        <div className="space-y-4">
                          {[
                            { icon: Globe, label: 'Website', value: profile?.website },
                            { icon: Mail, label: 'Email', value: profile?.email },
                            { icon: Phone, label: 'Phone', value: profile?.phone_contact },
                            { icon: MapPin, label: 'Address', value: profile?.address },
                          ].map((item, i) => item.value && (
                            <div key={i} className="flex items-start gap-3">
                              <div className="w-8 h-8 rounded-lg bg-zinc-50 flex items-center justify-center shrink-0">
                                <item.icon size={14} className="text-zinc-500" />
                              </div>
                              <div className="min-w-0">
                                <p className="text-[10px] text-zinc-400 uppercase tracking-wider">{item.label}</p>
                                <p className="text-sm font-medium text-zinc-900 truncate">{item.value}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {(profile?.languages || []).length > 0 && (
                        <div className="bg-white rounded-2xl border border-zinc-100 p-6">
                          <h3 className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold mb-3">Languages</h3>
                          <div className="flex flex-wrap gap-2">
                            {profile.languages.map(l => (
                              <span key={l} className="text-xs bg-purple-50 text-purple-700 px-3 py-1.5 rounded-lg font-medium">{l}</span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Team + Gallery Row */}
                  <div className="grid lg:grid-cols-2 gap-4">
                    {/* Team */}
                    <div className="bg-white rounded-2xl border border-zinc-100 p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Team Members</h3>
                        <Button onClick={() => setShowTeamDialog(true)} size="sm" className="bg-zinc-100 text-zinc-700 hover:bg-zinc-200 rounded-lg px-3 py-1.5 text-xs h-auto" data-testid="add-team-btn">
                          <UserPlus size={12} className="mr-1" /> Add
                        </Button>
                      </div>
                      {(profile?.team || []).length === 0 ? (
                        <p className="text-sm text-zinc-400 py-4 text-center">No team members added yet.</p>
                      ) : (
                        <div className="space-y-2.5">
                          {profile.team.map(m => (
                            <div key={m.id} className="flex items-center justify-between p-3 rounded-xl bg-zinc-50" data-testid={`team-${m.id}`}>
                              <div className="flex items-center gap-3">
                                <div className="w-9 h-9 rounded-xl bg-zinc-900 flex items-center justify-center text-white text-sm font-bold">
                                  {m.name.charAt(0).toUpperCase()}
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-zinc-900">{m.name}</p>
                                  <p className="text-xs text-zinc-400">{m.role}</p>
                                </div>
                              </div>
                              <button onClick={() => removeTeamMember(m.id)} className="p-1.5 hover:bg-red-50 rounded-lg text-zinc-400 hover:text-red-500 transition">
                                <Trash2 size={14} />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Gallery */}
                    <div className="bg-white rounded-2xl border border-zinc-100 p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Photo Gallery</h3>
                        <label className={`bg-zinc-100 text-zinc-700 hover:bg-zinc-200 rounded-lg px-3 py-1.5 text-xs font-medium cursor-pointer flex items-center gap-1 transition ${uploadingGallery ? 'opacity-50' : ''}`}>
                          <Image size={12} /> {uploadingGallery ? 'Uploading...' : 'Add Photo'}
                          <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={uploadGalleryPhoto} data-testid="gallery-upload" />
                        </label>
                      </div>
                      {(profile?.gallery || []).length === 0 ? (
                        <div className="text-center py-8 bg-zinc-50 rounded-xl">
                          <Image size={28} className="mx-auto mb-2 text-zinc-300" />
                          <p className="text-sm text-zinc-400">No photos yet.</p>
                        </div>
                      ) : (
                        <div className="grid grid-cols-3 gap-2.5">
                          {profile.gallery.map(photo => (
                            <div key={photo.id} className="relative group aspect-square rounded-xl overflow-hidden" data-testid={`gallery-${photo.id}`}>
                              <img src={getGalleryUrl(photo.path)} alt="" className="w-full h-full object-cover" />
                              <button
                                onClick={() => deleteGalleryPhoto(photo.id)}
                                className="absolute top-1.5 right-1.5 w-6 h-6 bg-black/60 rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition"
                              >
                                <X size={12} />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                /* Edit Mode */
                <div className="max-w-2xl" data-testid="profile-edit-form">
                  <div className="bg-white rounded-xl border border-neutral-100 p-6 space-y-5">
                    <div>
                      <Label className="text-sm font-medium text-neutral-700 mb-1.5 block">Company Name</Label>
                      <Input value={profileForm.company_name} onChange={e => setProfileForm(p => ({ ...p, company_name: e.target.value }))}
                        className="rounded-lg border-neutral-200" placeholder="Your company name" data-testid="edit-company-name" />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-neutral-700 mb-1.5 block">Description</Label>
                      <textarea value={profileForm.description} onChange={e => setProfileForm(p => ({ ...p, description: e.target.value }))}
                        className="w-full rounded-lg border border-neutral-200 px-3 py-2.5 text-sm min-h-[120px] resize-none focus:border-neutral-400 focus:ring-0"
                        placeholder="Tell travelers about your company..." data-testid="edit-description" />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm font-medium text-neutral-700 mb-1.5 block">License Number</Label>
                        <Input value={profileForm.license_number} onChange={e => setProfileForm(p => ({ ...p, license_number: e.target.value }))}
                          className="rounded-lg border-neutral-200" placeholder="e.g. TALA-12345" data-testid="edit-license" />
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-neutral-700 mb-1.5 block">Years of Experience</Label>
                        <Input type="number" value={profileForm.years_experience} onChange={e => setProfileForm(p => ({ ...p, years_experience: e.target.value }))}
                          className="rounded-lg border-neutral-200" placeholder="e.g. 10" data-testid="edit-experience" />
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm font-medium text-neutral-700 mb-1.5 block">Website</Label>
                        <Input value={profileForm.website} onChange={e => setProfileForm(p => ({ ...p, website: e.target.value }))}
                          className="rounded-lg border-neutral-200" placeholder="https://yourcompany.com" data-testid="edit-website" />
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-neutral-700 mb-1.5 block">Email</Label>
                        <Input value={profileForm.email} onChange={e => setProfileForm(p => ({ ...p, email: e.target.value }))}
                          className="rounded-lg border-neutral-200" placeholder="info@company.com" data-testid="edit-email" />
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm font-medium text-neutral-700 mb-1.5 block">Contact Phone</Label>
                        <Input value={profileForm.phone_contact} onChange={e => setProfileForm(p => ({ ...p, phone_contact: e.target.value }))}
                          className="rounded-lg border-neutral-200" placeholder="+255 xxx xxx xxx" data-testid="edit-phone" />
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-neutral-700 mb-1.5 block">Address</Label>
                        <Input value={profileForm.address} onChange={e => setProfileForm(p => ({ ...p, address: e.target.value }))}
                          className="rounded-lg border-neutral-200" placeholder="Arusha, Tanzania" data-testid="edit-address" />
                      </div>
                    </div>

                    {/* Services */}
                    <div>
                      <Label className="text-sm font-medium text-neutral-700 mb-2 block">Services Offered</Label>
                      <div className="flex flex-wrap gap-2">
                        {SERVICES_OPTIONS.map(s => (
                          <button key={s} type="button" onClick={() => toggleService(s)}
                            className={`text-sm px-3 py-1.5 rounded-full border transition ${
                              profileForm.services?.includes(s)
                                ? 'bg-[#073763] text-white border-[#073763]'
                                : 'bg-white text-neutral-600 border-neutral-200 hover:border-neutral-300'
                            }`} data-testid={`service-${s}`}>
                            {s}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Locations */}
                    <div>
                      <Label className="text-sm font-medium text-neutral-700 mb-2 block">Locations Covered</Label>
                      <div className="flex flex-wrap gap-2">
                        {LOCATIONS_OPTIONS.map(l => (
                          <button key={l} type="button" onClick={() => toggleLocation(l)}
                            className={`text-sm px-3 py-1.5 rounded-full border transition ${
                              profileForm.locations?.includes(l)
                                ? 'bg-blue-600 text-white border-blue-600'
                                : 'bg-white text-neutral-600 border-neutral-200 hover:border-neutral-300'
                            }`} data-testid={`location-${l}`}>
                            {l}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Languages */}
                    <div>
                      <Label className="text-sm font-medium text-neutral-700 mb-2 block">Languages Spoken</Label>
                      <div className="flex flex-wrap gap-2">
                        {['English', 'Swahili', 'French', 'German', 'Spanish', 'Italian', 'Chinese', 'Japanese', 'Arabic'].map(l => (
                          <button key={l} type="button" onClick={() => toggleLanguage(l)}
                            className={`text-sm px-3 py-1.5 rounded-full border transition ${
                              profileForm.languages?.includes(l)
                                ? 'bg-purple-600 text-white border-purple-600'
                                : 'bg-white text-neutral-600 border-neutral-200 hover:border-neutral-300'
                            }`} data-testid={`lang-${l}`}>
                            {l}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Bank Details */}
                    <div className="pt-4 border-t border-neutral-100">
                      <h4 className="text-sm font-semibold text-neutral-900 mb-1">Bank / Payment Details</h4>
                      <p className="text-xs text-neutral-400 mb-4">These details will appear on booking invoices you generate</p>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <Label className="text-xs font-medium text-neutral-500 mb-1.5 block">Bank Name</Label>
                          <Input value={profileForm.bank_name} onChange={e => setProfileForm(p => ({ ...p, bank_name: e.target.value }))}
                            className="rounded-lg border-neutral-200" placeholder="e.g. CRDB Bank" data-testid="edit-bank-name" />
                        </div>
                        <div>
                          <Label className="text-xs font-medium text-neutral-500 mb-1.5 block">Account Name</Label>
                          <Input value={profileForm.account_name} onChange={e => setProfileForm(p => ({ ...p, account_name: e.target.value }))}
                            className="rounded-lg border-neutral-200" placeholder="e.g. Safari Kings Ltd" data-testid="edit-account-name" />
                        </div>
                        <div>
                          <Label className="text-xs font-medium text-neutral-500 mb-1.5 block">Account Number</Label>
                          <Input value={profileForm.account_number} onChange={e => setProfileForm(p => ({ ...p, account_number: e.target.value }))}
                            className="rounded-lg border-neutral-200" placeholder="e.g. 01234567890" data-testid="edit-account-number" />
                        </div>
                        <div>
                          <Label className="text-xs font-medium text-neutral-500 mb-1.5 block">Currency</Label>
                          <Input value={profileForm.bank_currency} onChange={e => setProfileForm(p => ({ ...p, bank_currency: e.target.value }))}
                            className="rounded-lg border-neutral-200" placeholder="e.g. USD / TZS" data-testid="edit-bank-currency" />
                        </div>
                        <div>
                          <Label className="text-xs font-medium text-neutral-500 mb-1.5 block">SWIFT Code</Label>
                          <Input value={profileForm.swift_code} onChange={e => setProfileForm(p => ({ ...p, swift_code: e.target.value }))}
                            className="rounded-lg border-neutral-200" placeholder="e.g. CORUTZTZ" data-testid="edit-swift" />
                        </div>
                        <div>
                          <Label className="text-xs font-medium text-neutral-500 mb-1.5 block">Branch Code</Label>
                          <Input value={profileForm.branch_code} onChange={e => setProfileForm(p => ({ ...p, branch_code: e.target.value }))}
                            className="rounded-lg border-neutral-200" placeholder="e.g. 001" data-testid="edit-branch" />
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-3 pt-4 border-t border-neutral-100">
                      <Button onClick={saveProfile} disabled={submitting}
                        className="bg-[#073763] text-white hover:bg-[#052a4a] rounded-lg px-6 py-3 font-medium"
                        data-testid="save-profile-btn">
                        {submitting ? 'Saving...' : 'Save Changes'}
                      </Button>
                      <Button onClick={() => setEditingProfile(false)}
                        className="bg-white border border-neutral-200 text-neutral-700 hover:bg-neutral-50 rounded-lg px-6 py-3 font-medium">
                        Cancel
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ===== ITINERARY BUILDER VIEW ===== */}
        </main>
      </div>


      {/* ===== ADD TEAM MEMBER DIALOG ===== */}
      <Dialog open={showTeamDialog} onOpenChange={setShowTeamDialog}>
        <DialogContent className="max-w-md" data-testid="team-dialog">
          <DialogHeader>
            <DialogTitle>Add Team Member</DialogTitle>
            <DialogDescription>Add a guide or staff member to your team profile</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-sm font-medium text-neutral-700 mb-1.5 block">Full Name</Label>
              <Input value={teamForm.name} onChange={e => setTeamForm(p => ({ ...p, name: e.target.value }))}
                placeholder="e.g., John Moshi" className="rounded-lg border-neutral-200" data-testid="team-name" />
            </div>
            <div>
              <Label className="text-sm font-medium text-neutral-700 mb-1.5 block">Role</Label>
              <Input value={teamForm.role} onChange={e => setTeamForm(p => ({ ...p, role: e.target.value }))}
                placeholder="e.g., Senior Guide" className="rounded-lg border-neutral-200" data-testid="team-role" />
            </div>
            <div>
              <Label className="text-sm font-medium text-neutral-700 mb-1.5 block">Bio (optional)</Label>
              <textarea value={teamForm.bio} onChange={e => setTeamForm(p => ({ ...p, bio: e.target.value }))}
                className="w-full rounded-lg border border-neutral-200 px-3 py-2.5 text-sm min-h-[80px] resize-none"
                placeholder="Brief bio..." data-testid="team-bio" />
            </div>
            <Button onClick={addTeamMember} disabled={!teamForm.name || !teamForm.role}
              className="w-full bg-[#073763] text-white hover:bg-[#052a4a] rounded-lg py-2.5 font-medium"
              data-testid="add-team-submit">
              Add Team Member
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ===== CREATE TEAM ACCOUNT DIALOG ===== */}
      <Dialog open={showTeamAccountDialog} onOpenChange={setShowTeamAccountDialog}>
        <DialogContent className="max-w-md" data-testid="team-account-dialog">
          <DialogHeader>
            <DialogTitle>Create Team Account</DialogTitle>
            <DialogDescription>Create a login for your team member to access the platform</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-sm font-medium text-zinc-700 mb-1.5 block">Full Name</Label>
              <Input value={teamAccountForm.name} onChange={e => setTeamAccountForm(p => ({ ...p, name: e.target.value }))}
                placeholder="e.g., Sarah Moshi" className="rounded-lg border-zinc-200" data-testid="ta-name" />
            </div>
            <div>
              <Label className="text-sm font-medium text-zinc-700 mb-1.5 block">Phone Number</Label>
              <Input value={teamAccountForm.phone} onChange={e => setTeamAccountForm(p => ({ ...p, phone: e.target.value }))}
                placeholder="e.g., 255700111222" className="rounded-lg border-zinc-200" data-testid="ta-phone" />
            </div>
            <div>
              <Label className="text-sm font-medium text-zinc-700 mb-1.5 block">PIN (4-6 digits)</Label>
              <Input type="text" value={teamAccountForm.pin} onChange={e => setTeamAccountForm(p => ({ ...p, pin: e.target.value }))}
                placeholder="1234" maxLength={6} className="rounded-lg border-zinc-200" data-testid="ta-pin" />
            </div>
            <div>
              <Label className="text-sm font-medium text-zinc-700 mb-2 block">Assign Roles</Label>
              <div className="space-y-2">
                <button onClick={() => toggleTeamRole('leads_agent')}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg border transition text-left ${teamAccountForm.team_roles.includes('leads_agent') ? 'border-blue-300 bg-blue-50' : 'border-zinc-200 bg-white hover:bg-zinc-50'}`} data-testid="ta-role-leads">
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition ${teamAccountForm.team_roles.includes('leads_agent') ? 'border-blue-500 bg-blue-500' : 'border-zinc-300'}`}>
                    {teamAccountForm.team_roles.includes('leads_agent') && <Check size={12} className="text-white" />}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-zinc-900">Leads Agent</p>
                    <p className="text-[11px] text-zinc-400">Manage leads, create proposals, messaging</p>
                  </div>
                </button>
                <button onClick={() => toggleTeamRole('content_marketing')}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg border transition text-left ${teamAccountForm.team_roles.includes('content_marketing') ? 'border-purple-300 bg-purple-50' : 'border-zinc-200 bg-white hover:bg-zinc-50'}`} data-testid="ta-role-content">
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition ${teamAccountForm.team_roles.includes('content_marketing') ? 'border-purple-500 bg-purple-500' : 'border-zinc-300'}`}>
                    {teamAccountForm.team_roles.includes('content_marketing') && <Check size={12} className="text-white" />}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-zinc-900">Content / Marketing</p>
                    <p className="text-[11px] text-zinc-400">Curated itineraries, publishing, featuring</p>
                  </div>
                </button>
              </div>
            </div>
            <Button onClick={createTeamAccount} disabled={creatingTeamAccount || !teamAccountForm.name || !teamAccountForm.phone || teamAccountForm.pin.length < 4 || teamAccountForm.team_roles.length === 0}
              className="w-full bg-zinc-900 text-white hover:bg-zinc-800 rounded-lg py-2.5 font-medium" data-testid="confirm-create-team-account">
              {creatingTeamAccount ? 'Creating...' : 'Create Account'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ===== TOPUP DIALOG ===== */}
      <Dialog open={showTopupDialog} onOpenChange={(open) => { setShowTopupDialog(open); if (!open) setGeneratedInvoice(null); }}>
        <DialogContent className="max-w-md" data-testid="topup-dialog">
          {!generatedInvoice ? (
            <>
              <DialogHeader>
                <DialogTitle>Request Credit Top-Up</DialogTitle>
                <DialogDescription>Enter the number of credits you need. An invoice will be generated for payment.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-2">
                <div className="bg-neutral-50 rounded-lg p-4 text-center">
                  <p className="text-xs text-neutral-400 mb-1">Current Balance</p>
                  <p className="text-2xl font-bold text-neutral-900">{Math.floor(wallet?.balance || 0).toLocaleString()} <span className="text-sm font-normal text-neutral-400">credits</span></p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-neutral-700 mb-2 block">Number of Credits</Label>
                  <div className="grid grid-cols-4 gap-2 mb-3">
                    {['100', '500', '1000', '5000'].map(amt => (
                      <button key={amt} onClick={() => setTopupCredits(amt)}
                        className={`py-2.5 rounded-lg text-sm font-semibold transition ${
                          topupCredits === amt ? 'bg-[#073763] text-white' : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                        }`} data-testid={`topup-${amt}`}>
                        {parseInt(amt).toLocaleString()}
                      </button>
                    ))}
                  </div>
                  <Input type="number" value={topupCredits} onChange={e => setTopupCredits(e.target.value)}
                    placeholder="Enter custom amount" className="rounded-lg border-neutral-200" data-testid="topup-custom" />
                </div>
                {topupCredits && parseInt(topupCredits) > 0 && (
                  <div className="bg-blue-50 rounded-lg p-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-blue-700">Credits</span>
                      <span className="font-bold text-blue-900">{parseInt(topupCredits).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-sm mt-1">
                      <span className="text-blue-700">Amount Due</span>
                      <span className="font-bold text-blue-900">{wallet?.currency || 'USD'} {(parseInt(topupCredits) / (wallet?.credit_rate || 10)).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                    </div>
                    <div className="flex justify-between text-xs mt-1 pt-1 border-t border-blue-200">
                      <span className="text-blue-500">Rate</span>
                      <span className="text-blue-500">1 {wallet?.currency || 'USD'} = {wallet?.credit_rate || 10} credits</span>
                    </div>
                  </div>
                )}
                <Button onClick={topupWallet} disabled={submitting || !topupCredits || parseInt(topupCredits) <= 0}
                  className="w-full bg-[#073763] text-white hover:bg-[#052a4a] rounded-lg py-3 font-medium"
                  data-testid="confirm-topup">
                  {submitting ? <><Loader2 size={15} className="mr-1.5 animate-spin" /> Generating Invoice...</> : 'Generate Invoice'}
                </Button>
              </div>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>Invoice Generated</DialogTitle>
                <DialogDescription>Please make payment using the details below. Credits will be added once admin confirms payment.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-2">
                <div className="bg-[#073763] rounded-xl p-5 text-white">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <p className="text-xs text-blue-200 uppercase tracking-wider font-medium">Invoice</p>
                      <p className="text-lg font-bold mt-0.5">{generatedInvoice.invoice_number}</p>
                    </div>
                    <Badge className="bg-amber-400 text-amber-900 text-xs">Pending Payment</Badge>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-blue-200 text-xs">Credits</p>
                      <p className="font-bold">{generatedInvoice.credits_requested?.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-blue-200 text-xs">Amount Due</p>
                      <p className="font-bold">{generatedInvoice.currency} {generatedInvoice.amount?.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-blue-200 text-xs">Company</p>
                      <p className="font-medium text-xs">{generatedInvoice.company_name}</p>
                    </div>
                    <div>
                      <p className="text-blue-200 text-xs">Date</p>
                      <p className="font-medium text-xs">{generatedInvoice.created_at ? format(new Date(generatedInvoice.created_at), 'MMM dd, yyyy') : ''}</p>
                    </div>
                  </div>
                </div>
                {generatedInvoice.bank_details && (generatedInvoice.bank_details.bank_name || generatedInvoice.bank_details.account_number) && (
                  <div className="bg-neutral-50 rounded-xl p-4">
                    <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wider mb-3">Payment Details</p>
                    {generatedInvoice.bank_details.bank_name && (
                      <div className="flex justify-between text-sm mb-1.5">
                        <span className="text-neutral-500">Bank</span>
                        <span className="font-medium text-neutral-900">{generatedInvoice.bank_details.bank_name}</span>
                      </div>
                    )}
                    {generatedInvoice.bank_details.account_name && (
                      <div className="flex justify-between text-sm mb-1.5">
                        <span className="text-neutral-500">Account Name</span>
                        <span className="font-medium text-neutral-900">{generatedInvoice.bank_details.account_name}</span>
                      </div>
                    )}
                    {generatedInvoice.bank_details.account_number && (
                      <div className="flex justify-between text-sm mb-1.5">
                        <span className="text-neutral-500">Account Number</span>
                        <span className="font-medium text-neutral-900">{generatedInvoice.bank_details.account_number}</span>
                      </div>
                    )}
                    {generatedInvoice.bank_details.swift_code && (
                      <div className="flex justify-between text-sm mb-1.5">
                        <span className="text-neutral-500">SWIFT Code</span>
                        <span className="font-medium text-neutral-900">{generatedInvoice.bank_details.swift_code}</span>
                      </div>
                    )}
                    {generatedInvoice.bank_details.payment_instructions && (
                      <div className="mt-3 pt-3 border-t border-neutral-200">
                        <p className="text-xs text-neutral-500 font-medium mb-1">Payment Instructions</p>
                        <p className="text-sm text-neutral-700">{generatedInvoice.bank_details.payment_instructions}</p>
                      </div>
                    )}
                  </div>
                )}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-xs text-amber-700">Use <strong>{generatedInvoice.invoice_number}</strong> as payment reference. Credits will be added to your wallet once payment is confirmed by admin.</p>
                </div>
                <Button onClick={() => { setShowTopupDialog(false); setGeneratedInvoice(null); }}
                  className="w-full bg-neutral-900 text-white hover:bg-neutral-800 rounded-lg py-3 font-medium" data-testid="close-invoice">
                  Done
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ===== PROMOTE DIALOG ===== */}
      <Dialog open={showPromoteDialog} onOpenChange={setShowPromoteDialog}>
        <DialogContent className="max-w-sm" data-testid="promote-dialog">
          <DialogHeader>
            <DialogTitle>Promote Itinerary</DialogTitle>
            <DialogDescription>Feature your itinerary for increased visibility to travelers</DialogDescription>
          </DialogHeader>
          {promoteItinerary && (
            <div className="space-y-4 mt-2">
              <div className="bg-neutral-50 rounded-lg p-4">
                <h4 className="font-semibold text-neutral-900 text-sm">{promoteItinerary.title}</h4>
                <p className="text-xs text-neutral-400 mt-0.5">{promoteItinerary.duration_days} days &middot; ${promoteItinerary.total_price}</p>
              </div>
              <div>
                <Label className="text-sm font-medium text-neutral-700 mb-2 block">Promotion Duration</Label>
                <div className="grid grid-cols-3 gap-3">
                  {['promo_7', 'promo_14', 'promo_30'].map(key => {
                    const act = wallet?.activities?.[key];
                    if (!act || !act.enabled) return null;
                    const days = key.replace('promo_', '');
                    return (
                      <button key={key} onClick={() => setPromoDuration(parseInt(days))}
                        className={`py-3 rounded-xl text-center transition border-2 ${
                          promoDuration === parseInt(days) ? 'border-amber-400 bg-amber-50' : 'border-neutral-200 hover:border-neutral-300'
                        }`} data-testid={`promo-${days}d`}>
                        <p className="text-lg font-bold text-neutral-900">{act.cost}</p>
                        <p className="text-[10px] text-neutral-400">credits</p>
                        <p className="text-xs text-neutral-500">{days} days</p>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="flex items-center justify-between bg-neutral-50 rounded-lg p-3">
                <span className="text-sm text-neutral-500">Credit balance</span>
                <span className="font-semibold text-neutral-900">{Math.floor(wallet?.balance || 0).toLocaleString()} credits</span>
              </div>
              <Button onClick={promoteItineraryAction} disabled={submitting}
                className="w-full bg-amber-500 text-white hover:bg-amber-600 rounded-lg py-3 font-medium"
                data-testid="confirm-promote">
                {submitting ? 'Processing...' : `Promote for ${wallet?.activities?.[`promo_${promoDuration}`]?.cost || 0} credits`}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ===== PROFILE PREVIEW DIALOG ===== */}
      <Dialog open={!!profilePreview} onOpenChange={(open) => { if (!open) setProfilePreview(null); }}>
        <DialogContent className="max-w-md" data-testid="profile-preview-dialog">
          {profilePreview && (
            <>
              <DialogHeader>
                <DialogTitle>{profilePreview.type === 'traveler' ? 'Traveler Profile' : 'Operator Profile'}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-2">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-zinc-100 flex items-center justify-center text-xl font-bold text-zinc-400 overflow-hidden">
                    {profilePreview.avatar_url ? (
                      <img src={profilePreview.avatar_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      profilePreview.name?.charAt(0) || '?'
                    )}
                  </div>
                  <div>
                    <p className="font-bold text-zinc-900">{profilePreview.name}</p>
                    {profilePreview.company_name && <p className="text-sm text-zinc-500">{profilePreview.company_name}</p>}
                    <div className="flex items-center gap-2 mt-1">
                      {profilePreview.is_verified && <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px]">Verified</Badge>}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-zinc-50 rounded-lg p-3 text-center">
                    <p className="text-lg font-bold text-zinc-900">{profilePreview.total_trips || profilePreview.total_proposals || 0}</p>
                    <p className="text-[10px] text-zinc-400 uppercase tracking-wider">{profilePreview.type === 'traveler' ? 'Trips' : 'Proposals'}</p>
                  </div>
                  <div className="bg-zinc-50 rounded-lg p-3 text-center">
                    <p className="text-lg font-bold text-zinc-900">{profilePreview.total_bookings || 0}</p>
                    <p className="text-[10px] text-zinc-400 uppercase tracking-wider">Bookings</p>
                  </div>
                </div>
                {(profilePreview.phone || profilePreview.email) && (
                  <div className="bg-zinc-50 rounded-lg p-4 space-y-2">
                    <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">Contact</p>
                    {profilePreview.phone && <p className="text-sm text-zinc-700 flex items-center gap-2"><Phone size={14} className="text-zinc-400" /> {profilePreview.phone}</p>}
                    {profilePreview.email && <p className="text-sm text-zinc-700 flex items-center gap-2"><Mail size={14} className="text-zinc-400" /> {profilePreview.email}</p>}
                    {profilePreview.nationality && <p className="text-sm text-zinc-700 flex items-center gap-2"><Globe size={14} className="text-zinc-400" /> {profilePreview.nationality}</p>}
                  </div>
                )}
                {profilePreview.description && (
                  <div>
                    <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-1">About</p>
                    <p className="text-sm text-zinc-600">{profilePreview.description}</p>
                  </div>
                )}
                {profilePreview.services?.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-2">Services</p>
                    <div className="flex flex-wrap gap-1.5">
                      {profilePreview.services.map(s => <Badge key={s} className="bg-blue-50 text-blue-700 border border-blue-200 text-xs">{s}</Badge>)}
                    </div>
                  </div>
                )}
                {profilePreview.locations?.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-2">Locations</p>
                    <div className="flex flex-wrap gap-1.5">
                      {profilePreview.locations.map(l => <Badge key={l} className="bg-zinc-100 text-zinc-700 text-xs">{l}</Badge>)}
                    </div>
                  </div>
                )}
                {profilePreview.joined && (
                  <p className="text-xs text-zinc-400 text-center">Member since {format(new Date(profilePreview.joined), 'MMM yyyy')}</p>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ===== BOOKING INVOICE DIALOG ===== */}
      <Dialog open={!!invoiceDialog} onOpenChange={(open) => { if (!open) { setInvoiceDialog(null); } }}>
        <DialogContent className="max-w-md" data-testid="booking-invoice-dialog">
          <DialogHeader>
            <DialogTitle>Generate Booking Invoice</DialogTitle>
            <DialogDescription>Create an invoice for {invoiceDialog?.traveler_name} — {invoiceDialog?.itinerary_title}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs font-medium text-zinc-500 mb-1.5 block">Cost per person per day ($)</Label>
              <Input type="number" value={invoiceForm.cost_per_person_per_day} onChange={e => setInvoiceForm(p => ({ ...p, cost_per_person_per_day: e.target.value }))}
                placeholder="e.g. 250" className="rounded-lg border-neutral-200" data-testid="invoice-cost" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-medium text-zinc-500 mb-1.5 block">Number of people</Label>
                <Input type="number" value={invoiceForm.number_of_people} onChange={e => setInvoiceForm(p => ({ ...p, number_of_people: e.target.value }))}
                  placeholder="e.g. 4" className="rounded-lg border-neutral-200" data-testid="invoice-people" />
              </div>
              <div>
                <Label className="text-xs font-medium text-zinc-500 mb-1.5 block">Number of days</Label>
                <Input type="number" value={invoiceForm.number_of_days} onChange={e => setInvoiceForm(p => ({ ...p, number_of_days: e.target.value }))}
                  placeholder="e.g. 5" className="rounded-lg border-neutral-200" data-testid="invoice-days" />
              </div>
            </div>
            {invoiceForm.cost_per_person_per_day && invoiceForm.number_of_people && invoiceForm.number_of_days && (
              <div className="bg-[#073763] rounded-xl p-4 text-white">
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-blue-200">Cost/person/day</span>
                  <span className="font-bold">${parseFloat(invoiceForm.cost_per_person_per_day).toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-blue-200">{invoiceForm.number_of_people} people x {invoiceForm.number_of_days} days</span>
                  <span className="font-bold">{invoiceForm.number_of_people} x {invoiceForm.number_of_days}</span>
                </div>
                <div className="flex justify-between text-lg font-bold pt-2 mt-2 border-t border-white/20">
                  <span>Total</span>
                  <span>${(parseFloat(invoiceForm.cost_per_person_per_day) * parseInt(invoiceForm.number_of_people) * parseInt(invoiceForm.number_of_days)).toLocaleString()}</span>
                </div>
              </div>
            )}
            <div>
              <Label className="text-xs font-medium text-zinc-500 mb-1.5 block">Notes (optional)</Label>
              <textarea value={invoiceForm.notes} onChange={e => setInvoiceForm(p => ({ ...p, notes: e.target.value }))}
                placeholder="Any additional notes..." className="w-full min-h-[60px] p-3 rounded-lg border border-neutral-200 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-[#073763]" data-testid="invoice-notes" />
            </div>
            <Button onClick={() => generateBookingInvoice(invoiceDialog?.id)} disabled={submitting}
              className="w-full bg-[#073763] text-white hover:bg-[#052a4a] rounded-lg py-3 font-medium" data-testid="submit-invoice">
              {submitting ? <><Loader2 size={15} className="mr-1.5 animate-spin" /> Generating...</> : 'Generate Invoice'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ===== INVOICE PREVIEW / PRINT DIALOG ===== */}
      {/* ===== BOOKING INVOICE PREVIEW DIALOG ===== */}
      <Dialog open={!!invoicePreview} onOpenChange={(open) => { if (!open) setInvoicePreview(null); }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="invoice-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span>Invoice {invoicePreview?.invoice_number}</span>
                <Badge className={invoicePreview?.status === 'paid' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px]' : 'bg-amber-50 text-amber-700 border border-amber-200 text-[10px]'} data-testid="invoice-status-badge">
                  {invoicePreview?.status === 'paid' ? 'Paid' : 'Unpaid'}
                </Badge>
              </div>
              <div className="flex items-center gap-2">
                {invoicePreview?.status !== 'paid' && (
                  <Button onClick={() => markInvoicePaid(invoicePreview?.booking_id)} className="bg-emerald-600 text-white hover:bg-emerald-700 rounded-lg px-3 py-1.5 text-xs font-medium" data-testid="mark-paid-btn">
                    <Check size={14} className="mr-1" /> Mark Paid
                  </Button>
                )}
                <Button onClick={printInvoice} variant="outline" className="rounded-lg px-3 py-1.5 text-xs font-medium" data-testid="print-invoice">
                  <Printer size={14} className="mr-1.5" /> Print
                </Button>
              </div>
            </DialogTitle>
          </DialogHeader>
          {invoicePreview && (
            <div id="invoice-print-area" className="mt-2">
              {/* Header */}
              <div className="inv-header">
                <div>
                  <div className="inv-brand">safarinodes</div>
                  <div className="inv-tagline">Plan it. Match it. Explore it.</div>
                </div>
                <div style={{textAlign:'right'}}>
                  <div className="inv-number">{invoicePreview.invoice_number}</div>
                  <div className="inv-date">{invoicePreview.created_at ? format(new Date(invoicePreview.created_at), 'MMMM dd, yyyy') : ''}</div>
                  <div className={`inv-status ${invoicePreview.status === 'paid' ? 'paid' : 'unpaid'}`}>{invoicePreview.status === 'paid' ? 'Paid' : 'Unpaid'}</div>
                </div>
              </div>

              {/* From / Bill To */}
              <div className="inv-parties">
                <div className="inv-party">
                  <div className="inv-party-label">From</div>
                  <div className="inv-party-name">{invoicePreview.company_name}</div>
                </div>
                <div className="inv-party">
                  <div className="inv-party-label">Bill To</div>
                  <div className="inv-party-name">{invoicePreview.traveler_name}</div>
                  <div className="inv-party-detail">
                    {invoicePreview.traveler_phone && <>{invoicePreview.traveler_phone}<br /></>}
                    {invoicePreview.traveler_email && <>{invoicePreview.traveler_email}</>}
                  </div>
                </div>
              </div>

              {/* Booking Ref */}
              {invoicePreview.booking_ref && (
                <div className="inv-ref">
                  Booking Ref: <strong>{invoicePreview.booking_ref}</strong>{invoicePreview.itinerary_title && <> — {invoicePreview.itinerary_title}</>}
                </div>
              )}

              {/* Line Items Table */}
              <table className="inv-table">
                <thead>
                  <tr>
                    <th style={{textAlign:'left'}}>Description</th>
                    <th style={{textAlign:'center'}}>Pax</th>
                    <th style={{textAlign:'center'}}>Days</th>
                    <th style={{textAlign:'right'}}>Rate/Day</th>
                    <th style={{textAlign:'right'}}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Safari Package{invoicePreview.itinerary_title && <><br /><span style={{fontSize:'11px',color:'#888'}}>{invoicePreview.itinerary_title}</span></>}</td>
                    <td style={{textAlign:'center'}}>{invoicePreview.number_of_people}</td>
                    <td style={{textAlign:'center'}}>{invoicePreview.number_of_days}</td>
                    <td style={{textAlign:'right'}}>${invoicePreview.cost_per_person_per_day?.toLocaleString()}</td>
                    <td style={{textAlign:'right'}}>${invoicePreview.total_cost?.toLocaleString()}</td>
                  </tr>
                  <tr className="total-row">
                    <td colSpan={4} style={{textAlign:'right'}}>Total ({invoicePreview.currency || 'USD'})</td>
                    <td style={{textAlign:'right'}}>${invoicePreview.total_cost?.toLocaleString()}</td>
                  </tr>
                </tbody>
              </table>

              {/* Bank Details */}
              {invoicePreview.operator_bank_details && (invoicePreview.operator_bank_details.bank_name || invoicePreview.operator_bank_details.account_number) && (
                <div className="inv-bank">
                  <div className="inv-bank-title">Bank / Payment Details</div>
                  <div className="inv-bank-grid">
                    {invoicePreview.operator_bank_details.bank_name && (
                      <div className="inv-bank-item"><span className="inv-bank-label">Bank</span><strong>{invoicePreview.operator_bank_details.bank_name}</strong></div>
                    )}
                    {invoicePreview.operator_bank_details.account_name && (
                      <div className="inv-bank-item"><span className="inv-bank-label">Account Name</span><strong>{invoicePreview.operator_bank_details.account_name}</strong></div>
                    )}
                    {invoicePreview.operator_bank_details.account_number && (
                      <div className="inv-bank-item"><span className="inv-bank-label">Account No.</span><strong>{invoicePreview.operator_bank_details.account_number}</strong></div>
                    )}
                    {invoicePreview.operator_bank_details.swift_code && (
                      <div className="inv-bank-item"><span className="inv-bank-label">SWIFT Code</span><strong>{invoicePreview.operator_bank_details.swift_code}</strong></div>
                    )}
                    {invoicePreview.operator_bank_details.currency && (
                      <div className="inv-bank-item"><span className="inv-bank-label">Currency</span><strong>{invoicePreview.operator_bank_details.currency}</strong></div>
                    )}
                    {invoicePreview.operator_bank_details.branch_code && (
                      <div className="inv-bank-item"><span className="inv-bank-label">Branch</span><strong>{invoicePreview.operator_bank_details.branch_code}</strong></div>
                    )}
                  </div>
                  {invoicePreview.operator_bank_details.payment_instructions && (
                    <p style={{fontSize:'11px',color:'#555',marginTop:'10px',fontStyle:'italic'}}>{invoicePreview.operator_bank_details.payment_instructions}</p>
                  )}
                </div>
              )}

              {/* Notes */}
              {invoicePreview.notes && (
                <div className="inv-notes"><strong>Notes:</strong> {invoicePreview.notes}</div>
              )}

              {/* Footer */}
              <div className="inv-footer">
                <p><strong>safarinodes</strong> — Plan it. Match it. Explore it.</p>
                <p style={{marginTop:'4px'}}>Thank you for your business</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ===== CREDIT INVOICE PREVIEW DIALOG ===== */}
      <Dialog open={!!creditInvoicePreview} onOpenChange={(open) => { if (!open) setCreditInvoicePreview(null); }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="credit-invoice-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span>Credit Invoice {creditInvoicePreview?.invoice_number}</span>
                <Badge className={
                  creditInvoicePreview?.status === 'paid' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px]' :
                  creditInvoicePreview?.status === 'rejected' ? 'bg-red-50 text-red-700 border border-red-200 text-[10px]' :
                  'bg-amber-50 text-amber-700 border border-amber-200 text-[10px]'
                } data-testid="credit-invoice-status">
                  {creditInvoicePreview?.status === 'paid' ? 'Paid' : creditInvoicePreview?.status === 'rejected' ? 'Rejected' : 'Pending'}
                </Badge>
              </div>
              <Button onClick={() => {
                const el = document.getElementById('credit-invoice-print-area');
                if (!el) return;
                const w = window.open('', '_blank', 'width=800,height=600');
                w.document.write(`<html><head><title>Invoice ${creditInvoicePreview?.invoice_number || ''}</title>
                  <style>
                    *{margin:0;padding:0;box-sizing:border-box}
                    body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;margin:40px 48px;color:#1a1a1a;font-size:13px;line-height:1.5}
                    .inv-header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:28px;padding-bottom:16px;border-bottom:3px solid #073763}
                    .inv-brand{font-size:22px;font-weight:800;color:#073763;letter-spacing:-0.5px}
                    .inv-tagline{font-size:11px;color:#888;margin-top:2px;font-style:italic}
                    .inv-number{background:#073763;color:white;padding:5px 14px;border-radius:4px;font-size:12px;font-weight:700;display:inline-block}
                    .inv-date{font-size:11px;color:#888;margin-top:6px}
                    .inv-status{font-size:11px;font-weight:700;margin-top:4px;text-transform:uppercase;letter-spacing:0.5px}
                    .inv-status.paid{color:#059669}.inv-status.rejected{color:#dc2626}.inv-status.pending{color:#d97706}
                    .inv-parties{display:flex;gap:32px;margin-bottom:24px}
                    .inv-party{flex:1}
                    .inv-party-label{font-size:10px;text-transform:uppercase;letter-spacing:1.2px;color:#999;font-weight:700;margin-bottom:6px}
                    .inv-party-name{font-size:14px;font-weight:700;color:#1a1a1a}
                    .inv-party-detail{font-size:12px;color:#555;line-height:1.6}
                    .inv-table{width:100%;border-collapse:collapse;margin:20px 0}
                    .inv-table th{background:#073763;color:white;padding:10px 14px;font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:0.5px}
                    .inv-table th:first-child{border-radius:6px 0 0 0}.inv-table th:last-child{border-radius:0 6px 0 0}
                    .inv-table td{padding:11px 14px;border-bottom:1px solid #eee;font-size:13px;color:#333}
                    .inv-table .total-row td{border-top:2px solid #073763;font-weight:700;font-size:15px;background:#f0f4f8;color:#073763}
                    .inv-bank{background:#f0f7ff;padding:16px;border-radius:8px;margin:20px 0;border:1px solid #d0e3f7}
                    .inv-bank-title{font-size:10px;text-transform:uppercase;letter-spacing:1.2px;color:#073763;font-weight:700;margin-bottom:10px}
                    .inv-bank-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px}
                    .inv-bank-item{font-size:12px;color:#333}.inv-bank-label{color:#888;font-size:10px;display:block;margin-bottom:2px}
                    .inv-notice{background:#fffbeb;padding:12px 16px;border-radius:8px;margin:16px 0;border:1px solid #fde68a;font-size:12px;color:#92400e}
                    .inv-reject{background:#fef2f2;padding:12px 16px;border-radius:8px;margin:8px 0;border:1px solid #fecaca;font-size:12px;color:#991b1b}
                    .inv-footer{margin-top:36px;padding-top:16px;border-top:1px solid #e0e0e0;text-align:center;font-size:11px;color:#999}
                    @media print{body{margin:20px 28px}}
                  </style></head><body>`);
                w.document.write(el.innerHTML);
                w.document.write('</body></html>');
                w.document.close();
                w.focus();
                setTimeout(() => w.print(), 300);
              }} variant="outline" className="rounded-lg px-3 py-1.5 text-xs font-medium" data-testid="print-credit-invoice">
                <Printer size={14} className="mr-1.5" /> Print
              </Button>
            </DialogTitle>
          </DialogHeader>
          {creditInvoicePreview && (
            <div id="credit-invoice-print-area" className="mt-2">
              {/* Header */}
              <div className="inv-header">
                <div>
                  <div className="inv-brand">safarinodes</div>
                  <div className="inv-tagline">Credit Top-Up Invoice</div>
                </div>
                <div style={{textAlign:'right'}}>
                  <div className="inv-number">{creditInvoicePreview.invoice_number}</div>
                  <div className="inv-date">{creditInvoicePreview.created_at ? format(new Date(creditInvoicePreview.created_at), 'MMMM dd, yyyy') : ''}</div>
                  <div className={`inv-status ${creditInvoicePreview.status === 'paid' ? 'paid' : creditInvoicePreview.status === 'rejected' ? 'rejected' : 'pending'}`}>
                    {creditInvoicePreview.status === 'paid' ? 'Paid' : creditInvoicePreview.status === 'rejected' ? 'Rejected' : 'Pending'}
                  </div>
                </div>
              </div>

              {/* Operator / Platform */}
              <div className="inv-parties">
                <div className="inv-party">
                  <div className="inv-party-label">Operator</div>
                  <div className="inv-party-name">{creditInvoicePreview.company_name || creditInvoicePreview.operator_name}</div>
                  {creditInvoicePreview.operator_phone && <div className="inv-party-detail">{creditInvoicePreview.operator_phone}</div>}
                </div>
                <div className="inv-party">
                  <div className="inv-party-label">Platform</div>
                  <div className="inv-party-name">safarinodes</div>
                  <div className="inv-party-detail">Plan it. Match it. Explore it.</div>
                </div>
              </div>

              {/* Line Items Table */}
              <table className="inv-table">
                <thead>
                  <tr>
                    <th style={{textAlign:'left'}}>Description</th>
                    <th style={{textAlign:'center'}}>Credits</th>
                    <th style={{textAlign:'right'}}>Rate</th>
                    <th style={{textAlign:'right'}}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Platform Credits Purchase</td>
                    <td style={{textAlign:'center'}}>{creditInvoicePreview.credits_requested?.toLocaleString()}</td>
                    <td style={{textAlign:'right'}}>{creditInvoicePreview.credit_rate ? `${creditInvoicePreview.credit_rate} cr/${creditInvoicePreview.currency || 'USD'}` : '—'}</td>
                    <td style={{textAlign:'right'}}>{creditInvoicePreview.currency} {creditInvoicePreview.amount?.toLocaleString()}</td>
                  </tr>
                  <tr className="total-row">
                    <td colSpan={3} style={{textAlign:'right'}}>Total ({creditInvoicePreview.currency || 'USD'})</td>
                    <td style={{textAlign:'right'}}>{creditInvoicePreview.currency} {creditInvoicePreview.amount?.toLocaleString()}</td>
                  </tr>
                </tbody>
              </table>

              {/* Bank Details */}
              {creditInvoicePreview.bank_details && (creditInvoicePreview.bank_details.bank_name || creditInvoicePreview.bank_details.account_number) && (
                <div className="inv-bank">
                  <div className="inv-bank-title">Payment Details</div>
                  <div className="inv-bank-grid">
                    {creditInvoicePreview.bank_details.bank_name && (
                      <div className="inv-bank-item"><span className="inv-bank-label">Bank</span><strong>{creditInvoicePreview.bank_details.bank_name}</strong></div>
                    )}
                    {creditInvoicePreview.bank_details.account_name && (
                      <div className="inv-bank-item"><span className="inv-bank-label">Account Name</span><strong>{creditInvoicePreview.bank_details.account_name}</strong></div>
                    )}
                    {creditInvoicePreview.bank_details.account_number && (
                      <div className="inv-bank-item"><span className="inv-bank-label">Account No.</span><strong>{creditInvoicePreview.bank_details.account_number}</strong></div>
                    )}
                    {creditInvoicePreview.bank_details.swift_code && (
                      <div className="inv-bank-item"><span className="inv-bank-label">SWIFT Code</span><strong>{creditInvoicePreview.bank_details.swift_code}</strong></div>
                    )}
                  </div>
                  {creditInvoicePreview.bank_details.payment_instructions && (
                    <p style={{fontSize:'11px',color:'#555',marginTop:'10px',fontStyle:'italic'}}>{creditInvoicePreview.bank_details.payment_instructions}</p>
                  )}
                </div>
              )}

              {/* Payment Reference Notice */}
              <div className="inv-notice">
                Use <strong>{creditInvoicePreview.invoice_number}</strong> as payment reference. Credits will be added to your wallet once payment is confirmed by admin.
              </div>

              {/* Rejection */}
              {creditInvoicePreview.rejection_reason && (
                <div className="inv-reject"><strong>Rejection Reason:</strong> {creditInvoicePreview.rejection_reason}</div>
              )}

              {/* Footer */}
              <div className="inv-footer">
                <p><strong>safarinodes</strong> — Plan it. Match it. Explore it.</p>
                <p style={{marginTop:'4px'}}>Thank you for your business</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ===== REVIEW DIALOG ===== */}
      <Dialog open={!!reviewDialog} onOpenChange={(open) => { if (!open) setReviewDialog(null); }}>
        <DialogContent className="max-w-sm" data-testid="op-review-dialog">
          <DialogHeader>
            <DialogTitle>Review Traveler</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <p className="text-sm text-zinc-500">How was your experience with <strong>{reviewDialog?.traveler_name || 'this traveler'}</strong>?</p>
            <div className="flex gap-1" data-testid="op-review-stars">
              {[1,2,3,4,5].map(s => (
                <button key={s} onClick={() => setReviewRating(s)} className="p-0.5" data-testid={`op-star-${s}`}>
                  <Star size={28} className={s <= reviewRating ? 'fill-amber-400 text-amber-400' : 'text-zinc-200'} />
                </button>
              ))}
            </div>
            <textarea value={reviewComment} onChange={e => setReviewComment(e.target.value)} placeholder="Share your experience (optional)..." rows={3}
              className="w-full rounded-lg border border-zinc-200 px-3 py-2 text-sm" data-testid="op-review-comment" />
            <div className="flex gap-3">
              <Button onClick={submitReview} disabled={reviewSubmitting} className="flex-1 bg-[#073763] text-white hover:bg-[#052a4a] rounded-lg py-2.5 text-sm font-medium" data-testid="op-submit-review-btn">
                {reviewSubmitting ? 'Submitting...' : 'Submit Review'}
              </Button>
              <Button onClick={() => setReviewDialog(null)} variant="outline" className="rounded-lg py-2.5 text-sm">Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
